import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useWallet} from "@solana/wallet-adapter-react";
import {Link, useLocation, useParams} from "react-router-dom";
import { applyPageMetadata } from "../../utils/pageMetadata";
import { NotFoundPage, PageUnavailable } from "./PageStatus";
import { useGateAccess } from "../../hooks/useGateAccess";

import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    setDoc,
    serverTimestamp,
    startAfter,
    updateDoc,
    where,
} from "firebase/firestore";
import {onAuthStateChanged} from "firebase/auth";
import {ref, uploadBytesResumable, getDownloadURL} from "firebase/storage";
import {db, storage, auth, functions} from "../../firebaseConfig";
import ImageModal from "../ImageModal";
import ComponentAnalyticsSummary from "../ComponentAnalyticsSummary";
import ComponentAnalyticsTracker from "../ComponentAnalyticsTracker";
import PostStyleControls from "../PostStyleControls";
import SpaceFollowButton from "../SpaceFollowButton";
import bs58 from "bs58";
import {httpsCallable} from "firebase/functions";
import {signInWithCustomToken} from "firebase/auth";
import {openPublishSessionForWallet} from "../../utils/auth";
import {consumeSocialFeedShareDraft} from "../../utils/socialFeedShareDraft";
import {getYouTubeEmbedUrl} from "../../utils/youtubeEmbed";
import { DONATION_TOKENS } from "../../utils/donationTokens";
import { loadFollowingPublicKeys } from "../../utils/follows";
import * as htmlToImage from "html-to-image";

import "../MuultiStyle.css";

const PAGE_SIZE = 10;
const FEED_MODE_STORAGE_KEY = "muulti-social-feed-mode";
const FEED_SCOPE_STORAGE_KEY = "muulti-social-feed-scope";
const FEED_SEARCH_MAX_TERMS = 10;
const SEARCH_SCAN_BATCH_SIZE = 25;
const SEARCH_SCAN_MAX_DOCS = 250;
const DEFAULT_FEED_SEARCH_SCOPES = {
    keywords: true,
    tags: true,
    accounts: true,
};

// size rules
const MAX_IMAGE_MB = 50;
const MAX_AUDIO_MB = 50;
const MAX_VIDEO_MB = 100;
const MAX_IMAGES = 12;
const MAX_COMMENT_IMAGES = 3;
const SECRET_SUMMARY_DEFAULT = "Secret Message";
const HTTP_URL_PATTERN = /https?:\/\/[^\s]+/gi;
const HTTP_URL_FIRST_PATTERN = /https?:\/\/[^\s]+/i;
const URL_TRAILING_PUNCTUATION_PATTERN = /[),.!?;:'"]+$/;
const POST_REACTION_TYPES = [
    { type: "heart", label: "Heart", icon: "❤️" },
    { type: "clap", label: "Clap", icon: "👏" },
    { type: "brain", label: "Brain", icon: "🧠" },
    { type: "diamond", label: "Diamond", icon: "💎" },
    { type: "eyes", label: "Eyes", icon: "👀" },
    { type: "organHeart", label: "Celebration", icon: "🎉" },
    { type: "laugh", label: "Laugh", icon: "😂" },
];
const REACTION_TYPE_SET = new Set(POST_REACTION_TYPES.map((x) => x.type));

function emptyReactionCounts() {
    const next = {};
    POST_REACTION_TYPES.forEach((x) => {
        next[x.type] = 0;
    });
    return next;
}

function normalizeReactionType(type) {
    const value = String(type || "").trim();
    return REACTION_TYPE_SET.has(value) ? value : "";
}

function hasDonationGate(gateInput) {
    const gates = Array.isArray(gateInput?.gates) ? gateInput.gates : [];
    return gates.some((g) => g?.type === "donation" && String(g?.donationCompId || "").trim());
}
function bytesToMB(bytes) {
    return bytes / (1024 * 1024);
}

function clampSummaryFromContent(content, max = 100) {
    const t = (content || "").trim();
    if (!t) return "";
    return t.length > max ? t.slice(0, max) : t;
}

function makePreviewText({content = "", summary = ""}) {
    const base = (summary || content || "").trim();
    if (!base) return "";
    return base.length > 180 ? base.slice(0, 180) + "…" : base;
}

function stripTrailingUrlPunctuation(urlToken = "") {
    const token = String(urlToken || "");
    if (!token) return { href: "", trailingText: "" };
    const trailingMatch = token.match(URL_TRAILING_PUNCTUATION_PATTERN);
    if (!trailingMatch?.[0]) return { href: token, trailingText: "" };
    const trailingText = trailingMatch[0];
    const href = token.slice(0, token.length - trailingText.length);
    return { href, trailingText };
}

function extractFirstUrl(text = "") {
    const match = String(text || "").match(HTTP_URL_FIRST_PATTERN);
    if (!match?.[0]) return "";
    return stripTrailingUrlPunctuation(match[0]).href;
}

function createLinkifiedNodes(text = "", {linkStyle, keyPrefix = "link"} = {}) {
    const raw = String(text || "");
    if (!raw) return [];

    const nodes = [];
    let idx = 0;
    let lastIndex = 0;
    HTTP_URL_PATTERN.lastIndex = 0;
    let match = HTTP_URL_PATTERN.exec(raw);
    while (match) {
        const token = match[0];
        const start = match.index;
        const end = start + token.length;
        const { href, trailingText } = stripTrailingUrlPunctuation(token);

        if (start > lastIndex) nodes.push(raw.slice(lastIndex, start));

        if (href) {
            nodes.push(
                <a
                    key={`${keyPrefix}-${idx}`}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    style={linkStyle || undefined}
                >
                    {href}
                </a>
            );
            idx += 1;
        } else {
            nodes.push(token);
        }

        if (trailingText) nodes.push(trailingText);
        lastIndex = end;
        match = HTTP_URL_PATTERN.exec(raw);
    }

    if (lastIndex < raw.length) nodes.push(raw.slice(lastIndex));
    return nodes;
}

function renderLinkifiedParagraph(text = "", paragraphStyle, linkStyle, keyPrefix) {
    if (!text) return null;
    return (
        <p style={paragraphStyle || undefined}>
            {createLinkifiedNodes(text, {linkStyle, keyPrefix})}
        </p>
    );
}

function buildLinkMetadata(rawUrl = "") {
    const href = extractFirstUrl(rawUrl);
    if (!href) return null;
    try {
        const parsed = new URL(href);
        const host = String(parsed.hostname || "").replace(/^www\./i, "") || href;
        const path = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "";
        return {
            href,
            host,
            path: path.length > 60 ? `${path.slice(0, 60)}...` : path,
        };
    } catch {
        return {
            href,
            host: href,
            path: "",
        };
    }
}

function formatTs(ts) {
    if (!ts) return "";
    if (typeof ts?.toDate === "function") return ts.toDate().toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
    return "";
}

function readFeedModePreference() {
    try {
        const raw = localStorage.getItem(FEED_MODE_STORAGE_KEY);
        if (raw === "pagination" || raw === "infinite") return raw;
    } catch {}
    return "pagination";
}

function readFeedScopePreference() {
    try {
        const raw = localStorage.getItem(FEED_SCOPE_STORAGE_KEY);
        if (raw === "public" || raw === "following") return raw;
    } catch {}
    return "public";
}

function normalizeTagToken(tag = "") {
    return String(tag || "").trim().replace(/^#+/, "").trim().toLowerCase();
}

function parseTagsInput(input = "", maxTags = null) {
    const seen = new Set();
    const tags = [];

    String(input || "")
        .split(",")
        .forEach((rawTag) => {
            const tag = normalizeTagToken(rawTag);
            if (!tag || seen.has(tag)) return;
            seen.add(tag);
            tags.push(tag);
        });

    if (Number.isFinite(maxTags) && maxTags > 0) return tags.slice(0, maxTags);
    return tags;
}

function normalizeSearchToken(value = "") {
    return String(value || "").trim().replace(/^[#@]+/, "").trim().toLowerCase();
}

function parseSearchInput(input = "", maxTerms = FEED_SEARCH_MAX_TERMS) {
    const seen = new Set();
    const terms = [];

    String(input || "")
        .split(/[\s,]+/)
        .forEach((rawTerm) => {
            const term = normalizeSearchToken(rawTerm);
            if (!term || seen.has(term)) return;
            seen.add(term);
            terms.push(term);
        });

    return terms.slice(0, maxTerms);
}

function normalizeSearchText(value = "") {
    return String(value || "").toLowerCase();
}

function hasSelectedSearchScope(scopes = {}) {
    return Boolean(scopes.keywords || scopes.tags || scopes.accounts);
}

function normalizeSearchScopes(scopes = {}) {
    const next = {
        keywords: Boolean(scopes.keywords),
        tags: Boolean(scopes.tags),
        accounts: Boolean(scopes.accounts),
    };
    return hasSelectedSearchScope(next) ? next : { ...DEFAULT_FEED_SEARCH_SCOPES };
}

function getSearchScopeLabel(scopes = {}) {
    const labels = [];
    if (scopes.keywords) labels.push("keywords");
    if (scopes.tags) labels.push("tags");
    if (scopes.accounts) labels.push("accounts");
    return labels.join(", ");
}

function postIsVisibleToViewer(post, authUid) {
    return post?.status !== "draft" || post?.authorUid === authUid;
}

function postAllowsPublicContentSearch(post) {
    const gate = post?.gate || null;
    if (!gate || typeof gate !== "object") return true;

    const gates = Array.isArray(gate.gates) ? gate.gates : [];
    if (!gates.length) return true;

    const logic = gate.logic === "all" ? "all" : "any";
    if (logic === "any") return gates.some((g) => (g?.type || "public") === "public");
    return gates.every((g) => (g?.type || "public") === "public");
}

function withCurrentAuthorName(entry, profilesByUid) {
    const uid = String(entry?.authorUid || "").trim();
    const muultiName = String(profilesByUid[uid]?.muultiName || "").trim();
    return muultiName ? { ...entry, authorMuultiName: muultiName } : entry;
}

function postMatchesFeedSearch(post, terms = [], scopes = DEFAULT_FEED_SEARCH_SCOPES) {
    if (!terms.length) return true;
    const selectedScopes = normalizeSearchScopes(scopes);

    const tags = (Array.isArray(post?.object?.tags) ? post.object.tags : [])
        .map((tag) => normalizeTagToken(tag))
        .filter(Boolean);

    const keywordParts = [
        post?.preview?.text || "",
        post?.object?.summary || "",
    ];
    if (postAllowsPublicContentSearch(post)) {
        keywordParts.push(post?.object?.content || "");
    }
    const keywordText = normalizeSearchText(keywordParts.join(" "));

    const accountText = normalizeSearchText([
        post?.authorMuultiName || "",
        post?.authorUid || "",
    ].join(" "));

    return terms.some((term) => {
        if (selectedScopes.tags && tags.some((tag) => tag.includes(term))) return true;
        if (selectedScopes.keywords && keywordText.includes(term)) return true;
        if (selectedScopes.accounts && accountText.includes(term)) return true;
        return false;
    });
}

function numToPx(value) {
    if (typeof value === "number") return `${value}px`;
    return value || undefined;
}

function cleanStyle(style) {
    if (!style || typeof style !== "object") return null;
    const next = {};
    Object.entries(style).forEach(([key, value]) => {
        if (value === undefined || value === "" || value === null) return;
        next[key] = value;
    });
    return Object.keys(next).length ? next : null;
}

function normalizePostStyle(style) {
    if (!style || typeof style !== "object") return null;
    const next = { ...style };

    ["headers", "paragraph", "links"].forEach((scope) => {
        const cleaned = cleanStyle(style[scope]);
        if (cleaned) next[scope] = cleaned;
        else delete next[scope];
    });

    if (next.gradient && (!Array.isArray(next.gradient?.colors) || next.gradient.colors.length < 2)) {
        delete next.gradient;
    }

    Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === "" || next[key] === null) {
            delete next[key];
        }
    });

    return Object.keys(next).length ? next : null;
}

function buildPostStyleTokens(style = {}) {
    if (!style || typeof style !== "object") {
        return { panelStyle: null, headerStyle: null, paragraphStyle: null, linkStyle: null };
    }

    const panelStyle = {};
    const bgType = style.backgroundType || "";
    if (bgType === "gradient" && !style.backgroundImage && style.gradient?.colors?.length) {
        const dir = style.gradient.direction || "to right";
        const c0 = style.gradient.colors[0] || "#ffffff";
        const c1 = style.gradient.colors[1] || "#000000";
        panelStyle.backgroundImage = `linear-gradient(${dir}, ${c0}, ${c1})`;
    } else if (style.backgroundImage) {
        panelStyle.backgroundImage = style.backgroundImage;
    }
    if (style.backgroundColor) panelStyle.backgroundColor = style.backgroundColor;
    if (style.backgroundSize) panelStyle.backgroundSize = style.backgroundSize;
    if (style.backgroundPosition) panelStyle.backgroundPosition = style.backgroundPosition;
    if (style.repeatMode) panelStyle.backgroundRepeat = style.repeatMode;
    if (style.borderStyle) panelStyle.borderStyle = style.borderStyle;
    if (style.borderWidth != null) panelStyle.borderWidth = numToPx(style.borderWidth);
    if (style.borderColor) panelStyle.borderColor = style.borderColor;
    if (style.textAlign) panelStyle.textAlign = style.textAlign;
    if (style.color) panelStyle.color = style.color;

    const headerStyle = cleanStyle({
        fontFamily: style.headers?.fontFamily,
        fontSize: numToPx(style.headers?.fontSize),
        fontWeight: style.headers?.fontWeight,
        color: style.headers?.color,
        lineHeight: style.headers?.lineHeight,
    });

    const paragraphStyle = cleanStyle({
        fontFamily: style.paragraph?.fontFamily,
        fontSize: numToPx(style.paragraph?.fontSize),
        color: style.paragraph?.color,
        lineHeight: style.paragraph?.lineHeight,
    });

    const linkStyle = cleanStyle({
        color: style.links?.color,
        textDecoration: style.links?.textDecoration,
    });

    return {
        panelStyle: cleanStyle(panelStyle),
        headerStyle,
        paragraphStyle,
        linkStyle,
    };
}

function normalizeGateForPost(input) {
    const logic = input?.logic === "all" ? "all" : "any";
    const gates = (Array.isArray(input?.gates) ? input.gates : [{ type: "public" }])
        .map((g) => {
            const t = g?.type || "public";
            if (t === "public") return { type: "public" };

            if (t === "donation") {
                return {
                    type: "donation",
                    donationCompId: String(g?.donationCompId || ""),
                    donationThreshold: Number(g?.donationThreshold || 0),
                    campaignName: String(g?.campaignName || ""),
                    currency: String(g?.currency || ""),
                    tokenMint: String(g?.tokenMint || ""),
                };
            }

            if (t === "directPayment") {
                return {
                    type: "directPayment",
                    priceSol: Number(g?.priceSol || 0),
                    payToUid: String(g?.payToUid || ""),
                    targetId: String(g?.targetId || ""),
                    currency: String(g?.currency || "SOL"),
                    tokenMint: String(g?.tokenMint || ""),
                };
            }

            if (t === "tokensHeld") {
                return {
                    type: "tokensHeld",
                    tokenThreshold: Number(g?.tokenThreshold || 0),
                    currency: String(g?.currency || "SOL"),
                    tokenMint: String(g?.tokenMint || ""),
                };
            }

            if (t === "token" || t === "collection" || t === "compressedCollection") {
                return { type: t, listId: String(g?.listId || "") };
            }

            return null;
        })
        .filter(Boolean);

    return { logic, gates: gates.length ? gates : [{ type: "public" }] };
}

// Detect media “mode” from files
function detectMediaKind(files) {
    if (!files || files.length === 0) return "none";
    const kinds = new Set(files.map((f) => (f.type || "").split("/")[0]));
    if (kinds.size !== 1) return "mixed";
    const k = [...kinds][0];
    if (k === "image") return "image";
    if (k === "video") return "video";
    if (k === "audio") return "audio";
    return "other";
}

function LogFeedImage({image, name, onOpen}) {
    return (
        <img
            className="logfeed-post-image"
            src={image}
            alt={name}
            onClick={onOpen}
        />
    );
}


// Map media kind to ActivityStreams-ish object.type
function objectTypeFromMediaKind(mediaKind, hasText) {
    if (mediaKind === "image") return "Image";
    if (mediaKind === "video") return "Video";
    if (mediaKind === "audio") return "Audio";
    return hasText ? "Note" : "Note";
}




function LogFeedPostCard({
                             p,
                             isOwnerOfPost,
                             beginEditPost,
                             beginComposerEditPost,
                             setPostCardRef,
                             handleCopyPostCard,
                             handleDownloadPostCard,
                             sharingPostId,
                             handleDeletePost,
                             openMenuPostId,
                             setOpenMenuPostId,
                             editingPostId,
                             cancelEditPost,
                             handleSaveEditPost,
                             editContent,
                             setEditContent,
                             editSummaryOverride,
                             setEditSummaryOverride,
                             editTagsInput,
                             setEditTagsInput,
                             editErr,
                             editLoading,
                             formatTs,
                             makePreviewText,
                             setModalImage,
                             LogFeedImage,
                             showCommentsByPost,
                             setShowCommentsByPost,
                             loadCommentsForPost,
                             commentsByPost,
                             commentsLoadingByPost,
                             commentErrByPost,
                             canComment,
                             canReact,
                             newCommentByPost,
                             setNewCommentByPost,
                             newCommentFilesByPost,
                             setNewCommentFilesByPost,
                             handleCreateComment,
                             editingCommentByPost,
                             beginEditComment,
                             cancelEditComment,
                             handleSaveEditComment,
                             handleDeleteComment,
                             editCommentTextByPost,
                             setEditCommentTextByPost,
                             editCommentErrByPost,
                             editCommentLoadingByPost,
                             openMenuCommentKey,
                             setOpenMenuCommentKey,
                             isOwnerOfComment,
                             reactionsByPost,
                             reactionsLoadingByPost,
                             reactionErrByPost,
                             loadReactionsForPost,
                             handleSetReaction,
                             currentUid,
                             authorProfilePicUrl,
                             getPostDetailPath,
                             handleSharePostLink,
                             sharingLinkPostId,
                             linkCopiedPostId,
                             isDetailView,
                             donationLookup,
                             onSearchTag,
                             showFollowerCountForAuthor,
                             followerMuultiName,
                         }) {
    // ✅ real gating
    const [hasAccess, gateLoading, gateName] = useGateAccess(
        p?.gate ? { ...p.gate, donationLookup } : p?.gate, { componentId: p.id, spaceOwnerUid: p.authorUid }
    );

    const isEditing = editingPostId === p.id;
    const isGated = !hasAccess;
    const canShareCard = !isEditing && p?.status !== "draft";
    const { panelStyle, headerStyle, paragraphStyle, linkStyle } = useMemo(
        () => buildPostStyleTokens(p?.style),
        [p?.style]
    );
    const paragraphStyleWithBreaks = paragraphStyle
        ? { ...paragraphStyle, whiteSpace: "pre-wrap" }
        : { whiteSpace: "pre-wrap" };

    const createdLabel = formatTs(p.createdAt);
    const updatedLabel = formatTs(p.updatedAt);
    const authorLink = p.authorMuultiName ? `/${p.authorMuultiName}` : "#";

// ✅ SAFE: never derive preview from full content when gated.
// If locked, we only show summary/preview fields, not p.object.content.
    const safePreviewText =
        (p.preview?.text || "").trim() ||
        (p.object?.summary || "").trim(); // summary is allowed; content is not

    const postMedia = Array.isArray(p.object?.media)
        ? p.object.media.filter((m) => String(m?.url || "").trim())
        : [];
    const media0 = postMedia[0]?.url;
    const media0MimeType = String(postMedia[0]?.mimeType || "");
    const imageMedia = postMedia.filter((m) => String(m?.mimeType || "").startsWith("image/"));
    const primaryUrl = extractFirstUrl(p.object?.content || "") || String(p.object?.url || "");
    const youtubeEmbedUrl = getYouTubeEmbedUrl(primaryUrl);
    const nonYouTubeLinkMetadata = !youtubeEmbedUrl
        ? buildLinkMetadata(primaryUrl)
        : null;
    const tags = Array.isArray(p.object?.tags) ? p.object.tags : [];
    const displayTags = [];
    tags.forEach((tag) => {
        const normalizedTag = normalizeTagToken(tag);
        if (!normalizedTag || displayTags.includes(normalizedTag)) return;
        displayTags.push(normalizedTag);
    });
    const detailPath = getPostDetailPath ? getPostDetailPath(p) : "";
    const isDraftPost = p?.status === "draft" || p?.id === "local-draft";

    const reactionState = reactionsByPost?.[p.id] || null;
    const reactionCounts = reactionState?.counts || emptyReactionCounts();
    const myReaction = currentUid ? reactionState?.byUid?.[currentUid]?.type || "" : "";
    const reactionsLoading = Boolean(reactionsLoadingByPost?.[p.id]);
    const reactionErr = reactionErrByPost?.[p.id] || "";

    const commentsOpen = (showCommentsByPost?.[p.id] ?? isDetailView) && !isDraftPost;
    const comments = commentsByPost?.[p.id] || [];
    const commentsLoading = Boolean(commentsLoadingByPost?.[p.id]);
    const commentErr = commentErrByPost?.[p.id] || "";
    const newCommentText = newCommentByPost?.[p.id] || "";
    const newCommentFiles = newCommentFilesByPost?.[p.id] || [];
    const editingCommentId = editingCommentByPost?.[p.id] || null;
    const editCommentText = editCommentTextByPost?.[p.id] || "";
    const editCommentErr = editCommentErrByPost?.[p.id] || "";
    const editCommentLoading = Boolean(editCommentLoadingByPost?.[p.id]);
    const analyticsPostComp = useMemo(() => {
        if (isDraftPost || !p?.id || !p?.authorUid) return null;
        return {
            id: p.id,
            type: "post",
            ownerUid: p.authorUid,
            ownerPublicKey: p.authorUid,
            muultiName: p.authorMuultiName || "",
        };
    }, [isDraftPost, p?.authorMuultiName, p?.authorUid, p?.id]);
    const analyticsPostOwner = String(p?.authorUid || "").trim();
    const analyticsPostMuulti = useMemo(() => ({
        publicKey: analyticsPostOwner,
        muultiName: p?.authorMuultiName || "",
    }), [analyticsPostOwner, p?.authorMuultiName]);

    useEffect(() => {
        if (isDraftPost || !p?.id) return;
        if (reactionsByPost?.[p.id] || reactionsLoadingByPost?.[p.id]) return;
        loadReactionsForPost?.(p.id);
    }, [isDraftPost, p?.id, reactionsByPost, reactionsLoadingByPost, loadReactionsForPost]);

    useEffect(() => {
        if (!commentsOpen || isDraftPost || !p?.id) return;
        if (commentsByPost?.[p.id] || commentsLoadingByPost?.[p.id]) return;
        loadCommentsForPost?.(p.id);
    }, [commentsOpen, isDraftPost, p?.id, commentsByPost, commentsLoadingByPost, loadCommentsForPost]);

    const toggleComments = () => {
        if (!p?.id || isDraftPost) return;
        const nextOpen = !commentsOpen;
        setShowCommentsByPost?.((prev) => ({ ...prev, [p.id]: nextOpen }));
        if (nextOpen && !commentsByPost?.[p.id] && !commentsLoadingByPost?.[p.id]) {
            loadCommentsForPost?.(p.id);
        }
    };

    return (
        <div
            ref={(el) => setPostCardRef?.(p.id, el)}
            key={p.id}
            className={`component-item logfeed-post ${isGated ? "locked" : ""}`}
            style={panelStyle || undefined}
        >
            <ComponentAnalyticsTracker
                comp={analyticsPostComp}
                muulti={analyticsPostMuulti}
                surface={isDetailView ? "social-post-detail" : "social-feed"}
                trackImpression={!isDraftPost}
                trackView={!isDraftPost && isDetailView}
                style={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
            {/* top-right ellipsis menu (owner only) */}
            {isOwnerOfPost(p) && (
                <div className="logfeed-post-menu" data-post-menu-id={p.id}>
                    <button
                        type="button"
                        className="logfeed-ellipsis-btn"
                        aria-haspopup="menu"
                        aria-expanded={openMenuPostId === p.id}
                        aria-label="Post actions"
                        onClick={() => setOpenMenuPostId((cur) => (cur === p.id ? null : p.id))}
                    >
                        …
                    </button>

                            {openMenuPostId === p.id && (
                                <div className="logfeed-menu-dropdown" role="menu">
                                    <button
                                        type="button"
                                        className="logfeed-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setOpenMenuPostId(null);
                                            beginEditPost(p);
                                        }}
                                    >
                                        Quick Edit
                                    </button>

                                    <button
                                        type="button"
                                        className="logfeed-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setOpenMenuPostId(null);
                                            beginComposerEditPost(p);
                                        }}
                                    >
                                        Edit in Composer
                                    </button>

                                    <button
                                        type="button"
                                        className="logfeed-menu-item danger"
                                        role="menuitem"
                                onClick={() => {
                                    setOpenMenuPostId(null);
                                    handleDeletePost(p.id);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* CONTENT */}
            {isEditing ? (
                <div className="logfeed-edit">
                    <label>
                        Content
                        <textarea className="muulti-textarea" value={editContent} onChange={(e) => setEditContent(e.target.value)} />
                    </label>

                    <label>
                        Summary (optional)
                        <textarea
                            className="muulti-textarea"
                            value={editSummaryOverride}
                            onChange={(e) => setEditSummaryOverride(e.target.value)}
                        />
                    </label>

                    <label>
                        Tags (comma separated)
                        <input className="muulti-input" value={editTagsInput} onChange={(e) => setEditTagsInput(e.target.value)} />
                    </label>

                    {editErr && <p className="error">{editErr}</p>}

                    <div className="muulti-container-button-container">
                        <button className="muulti-button" disabled={editLoading} onClick={() => handleSaveEditPost(p.id)}>
                            {editLoading ? "Saving…" : "Save"}
                        </button>
                        <button className="muulti-button" onClick={cancelEditPost}>
                            Cancel
                        </button>
                    </div>
                </div>
            ) : isGated ? (
                <div className="logfeed-content">
                    {safePreviewText ? (
                        renderLinkifiedParagraph(
                            safePreviewText,
                            paragraphStyleWithBreaks,
                            linkStyle,
                            `preview-${p.id}`
                        )
                    ) : (
                        <p style={paragraphStyle || undefined}><em>No preview available.</em></p>
                    )}
                    <p style={paragraphStyle || undefined}>
                        <strong>Locked:</strong>{" "}
                        {gateLoading ? "Checking access…" : `This entry is gated${gateName ? ` (${gateName})` : ""}.`}
                    </p>
                </div>
            ) : (
                <div className="logfeed-content">
                    <div className="logfeed-author-line">
                        {authorProfilePicUrl ? (
                            <img
                                className="logfeed-author-avatar"
                                src={authorProfilePicUrl}
                                alt={p.authorMuultiName ? `${p.authorMuultiName} profile` : "Author profile"}
                            />
                        ) : null}
                        <h3 style={headerStyle || undefined}>
                            {p.authorMuultiName ? (
                                <Link to={authorLink} style={linkStyle || undefined}>
                                    {p.authorMuultiName}
                                </Link>
                            ) : (
                                "Unknown"
                            )}
                        </h3>
                    </div>
                    {p.object?.content ? (
                        renderLinkifiedParagraph(
                            p.object.content,
                            paragraphStyleWithBreaks,
                            linkStyle,
                            `post-${p.id}`
                        )
                    ) : null}
                </div>
            )}



            {/* Embedded previews only if unlocked */}
            {!isGated && youtubeEmbedUrl && (
                <div className="logfeed-embed">
                    <div className="logfeed-embed-frame">
                        <iframe
                            src={youtubeEmbedUrl}
                            title="YouTube video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}
            {!isGated && nonYouTubeLinkMetadata && (
                <div className="logfeed-embed">
                    <div style={{padding: "12px 14px"}}>
                        <div style={{fontSize: "0.8rem", opacity: 0.8}}>{nonYouTubeLinkMetadata.host}</div>
                        <a
                            href={nonYouTubeLinkMetadata.href}
                            target="_blank"
                            rel="noreferrer"
                            style={linkStyle || undefined}
                        >
                            {nonYouTubeLinkMetadata.path || nonYouTubeLinkMetadata.href}
                        </a>
                    </div>
                </div>
            )}

            {/* MEDIA */}
            {!isGated && media0 ? (
                <div className="logfeed-media">
                    {media0MimeType.startsWith("image/") ? (
                        imageMedia.length > 1 ? (
                            <div className="logfeed-image-gallery">
                                {imageMedia.map((media, idx) => (
                                    <button
                                        key={`${media.url}-${idx}`}
                                        type="button"
                                        className="logfeed-gallery-item"
                                        onClick={() =>
                                            setModalImage({
                                                image: media.url,
                                                name: media.name || `Post media ${idx + 1}`,
                                            })
                                        }
                                        aria-label={`Open image ${idx + 1}`}
                                    >
                                        <img
                                            className="logfeed-gallery-image"
                                            src={media.url}
                                            alt={media.name || `Post media ${idx + 1}`}
                                        />
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="logfeed-image-container">
                                <LogFeedImage
                                    image={media0}
                                    name={postMedia[0]?.name || "Post media"}
                                    onOpen={() =>
                                        setModalImage({
                                            image: media0,
                                            name: postMedia[0]?.name || "Post media",
                                        })
                                    }
                                />
                            </div>
                        )
                    ) : media0MimeType.startsWith("video/") ? (
                        <div className="logfeed-video-container">
                            <video className="logfeed-post-video" controls preload="metadata">
                                <source
                                    src={media0}
                                    type={postMedia[0]?.mimeType || "video/mp4"}
                                />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    ) : media0MimeType.startsWith("audio/") ? (
                        <div className="logfeed-audio-container">
                            <audio className="logfeed-post-audio" controls preload="metadata">
                                <source
                                    src={media0}
                                    type={postMedia[0]?.mimeType || "audio/mpeg"}
                                />
                                Your browser does not support the audio element.
                            </audio>
                        </div>
                    ) : (
                        <a href={media0} target="_blank" rel="noreferrer">
                            Open media
                        </a>
                    )}
                </div>
            ) : null}

            {/* META */}
            <div className="logfeed-post-header">
                <div>
                    <h3 style={headerStyle || undefined}>
                        {p.object?.type || "Note"} {isGated ? <span className="logfeed-lock">🔒</span> : null}
                    </h3>
                </div>
            </div>

            {displayTags.length > 0 && !isGated && (
                <div className="logfeed-tags" style={paragraphStyle || undefined}>
                    {displayTags.map((tag) => (
                        onSearchTag ? (
                            <button
                                key={tag}
                                type="button"
                                className="logfeed-tag"
                                onClick={() => onSearchTag(tag)}
                            >
                                #{tag}
                            </button>
                        ) : (
                            <span key={tag} className="logfeed-tag">
                                #{tag}
                            </span>
                        )
                    ))}
                </div>
            )}


            <div className="logfeed-meta" style={paragraphStyle || undefined}>
                {createdLabel ? <span className="logfeed-meta-tag">Created: {createdLabel}</span> : null}
                {updatedLabel ? <span className="logfeed-meta-tag">Updated: {updatedLabel}</span> : null}
                {(canShareCard || (!isDraftPost && p?.authorUid)) && (
                    <span className="logfeed-meta-actions">
                        {!isDraftPost && p?.authorUid ? (
                            <SpaceFollowButton
                                targetPublicKey={p.authorUid}
                                targetMuultiName={p.authorMuultiName || ""}
                                followerMuultiName={followerMuultiName}
                                showFollowerCount={showFollowerCountForAuthor === true}
                                compact
                            />
                        ) : null}
                        {canShareCard && (
                            <>
                        <button
                            type="button"
                            className="muulti-button logfeed-meta-button"
                            onClick={() => handleDownloadPostCard?.(p)}
                            disabled={sharingPostId === p.id}
                        >
                            {sharingPostId === p.id ? "Working..." : "Download PNG"}
                        </button>
                        <button
                            type="button"
                            className="muulti-button logfeed-meta-button"
                            onClick={() => handleCopyPostCard?.(p)}
                            disabled={sharingPostId === p.id}
                        >
                            {sharingPostId === p.id ? "Working..." : "Copy PNG"}
                        </button>
                        {detailPath && !isDetailView && (
                            <Link
                                className="muulti-button logfeed-meta-button"
                                to={detailPath}
                            >
                                Open Detail
                            </Link>
                        )}
                        {detailPath && (
                            <button
                                type="button"
                                className="muulti-button logfeed-meta-button"
                                onClick={() => handleSharePostLink?.(p)}
                                disabled={sharingLinkPostId === p.id}
                            >
                                {sharingLinkPostId === p.id
                                    ? "Sharing..."
                                    : linkCopiedPostId === p.id
                                        ? "Copied!"
                                    : "Share Link"}
                            </button>
                        )}
                            </>
                        )}
                    </span>
                )}
            </div>

            {!isDraftPost && (
                <ComponentAnalyticsSummary
                    comp={analyticsPostComp}
                    ownerPublicKey={analyticsPostOwner}
                    title="Post activity"
                    compact
                    footer
                />
            )}

            {!isDraftPost && (
                <div className="logfeed-comments">
                    <div className="logfeed-reactions">
                        <div className="logfeed-reactions-grid">
                            {POST_REACTION_TYPES.map((reaction) => {
                                const count = reactionCounts?.[reaction.type] || 0;
                                const isActive = myReaction === reaction.type;
                                return (
                                    <button
                                        key={`${p.id}-${reaction.type}`}
                                        type="button"
                                        className={`muulti-button logfeed-reaction-btn ${isActive ? "active" : ""}`}
                                        onClick={() => handleSetReaction?.(p.id, reaction.type)}
                                        disabled={reactionsLoading}
                                        aria-label={`${reaction.label}: ${count} ${count === 1 ? "reaction" : "reactions"}${isActive ? " selected" : ""}`}
                                        title={reaction.label}
                                    >
                                        <span className="logfeed-reaction-icon" aria-hidden="true">
                                            {reaction.icon}
                                        </span>
                                        <span className="logfeed-reaction-count">{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                        {reactionErr ? <p className="error">{reactionErr}</p> : null}
                        {!canReact && (
                            <p className="logfeed-reaction-hint">
                                Connect wallet, sign wallet authority, and create your MuultiSpace profile to react.
                            </p>
                        )}
                    </div>

                    <div className="logfeed-comments-actions">
                        <button
                            type="button"
                            className="muulti-button"
                            onClick={toggleComments}
                            disabled={commentsLoading}
                        >
                            {commentsOpen ? "Hide Comments" : `Show Comments (${comments.length})`}
                        </button>
                    </div>

                    {commentsOpen && (
                        <>
                            {commentErr ? <p className="error">{commentErr}</p> : null}
                            {commentsLoading ? <p>Loading comments...</p> : null}

                            {!commentsLoading && comments.map((c) => {
                                const commentMenuKey = `${p.id}:${c.id}`;
                                const isEditingComment = editingCommentId === c.id;
                                const commentAuthorPath = c?.authorMuultiName ? `/${c.authorMuultiName}` : "";
                                const commentMedia = Array.isArray(c?.media) ? c.media : [];

                                return (
                                    <div key={c.id} className="logfeed-comment">
                                        <div className="logfeed-comment-top">
                                            <div className="logfeed-comment-meta">
                                                {commentAuthorPath ? (
                                                    <strong>
                                                        <Link to={commentAuthorPath} style={linkStyle || undefined}>
                                                            {c.authorMuultiName}
                                                        </Link>
                                                    </strong>
                                                ) : (
                                                    <strong>{c.authorMuultiName || "Unknown"}</strong>
                                                )}
                                                <span>{formatTs(c.createdAt)}</span>
                                            </div>

                                            {isOwnerOfComment(c) && (
                                                <div className="logfeed-comment-menu" data-comment-menu-key={commentMenuKey}>
                                                    <button
                                                        type="button"
                                                        className="logfeed-ellipsis-btn"
                                                        aria-haspopup="menu"
                                                        aria-expanded={openMenuCommentKey === commentMenuKey}
                                                        aria-label="Comment actions"
                                                        onClick={() =>
                                                            setOpenMenuCommentKey((cur) =>
                                                                cur === commentMenuKey ? null : commentMenuKey
                                                            )
                                                        }
                                                    >
                                                        ...
                                                    </button>
                                                    {openMenuCommentKey === commentMenuKey && (
                                                        <div className="logfeed-menu-dropdown" role="menu">
                                                            <button
                                                                type="button"
                                                                className="logfeed-menu-item"
                                                                role="menuitem"
                                                                onClick={() => {
                                                                    setOpenMenuCommentKey(null);
                                                                    beginEditComment(p.id, c);
                                                                }}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="logfeed-menu-item danger"
                                                                role="menuitem"
                                                                onClick={() => {
                                                                    setOpenMenuCommentKey(null);
                                                                    handleDeleteComment(p.id, c.id);
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {isEditingComment ? (
                                            <div className="logfeed-comment-edit">
                                                <textarea
                                                    className="muulti-textarea"
                                                    value={editCommentText}
                                                    onChange={(e) =>
                                                        setEditCommentTextByPost((prev) => ({
                                                            ...prev,
                                                            [p.id]: e.target.value,
                                                        }))
                                                    }
                                                />
                                                {editCommentErr ? <p className="error">{editCommentErr}</p> : null}
                                                <div className="muulti-container-button-container">
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        disabled={editCommentLoading}
                                                        onClick={() => handleSaveEditComment(p.id)}
                                                    >
                                                        {editCommentLoading ? "Saving..." : "Save"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="muulti-button"
                                                        onClick={() => cancelEditComment(p.id)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {c.content ? (
                                                    renderLinkifiedParagraph(
                                                        c.content,
                                                        paragraphStyleWithBreaks,
                                                        linkStyle,
                                                        `comment-${p.id}-${c.id}`
                                                    )
                                                ) : null}

                                                {commentMedia.length > 0 && (
                                                    <div className="logfeed-comment-media">
                                                        {commentMedia.map((m, idx) => {
                                                            const url = String(m?.url || "");
                                                            const mimeType = String(m?.mimeType || "");
                                                            const isImage = mimeType.startsWith("image/");
                                                            if (!url) return null;
                                                            if (isImage) {
                                                                return (
                                                                    <img
                                                                        key={`${c.id}-media-${idx}`}
                                                                        src={url}
                                                                        alt={m?.name || "Comment media"}
                                                                        className="logfeed-comment-image"
                                                                        onClick={() => {
                                                                            setModalImage({
                                                                                image: url,
                                                                                name: m?.name || "Comment media",
                                                                            });
                                                                        }}
                                                                    />
                                                                );
                                                            }
                                                            return (
                                                                <a
                                                                    key={`${c.id}-media-${idx}`}
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    Open media
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}

                            {!commentsLoading && comments.length === 0 && (
                                <p>No comments yet.</p>
                            )}

                            <div className="logfeed-comment-composer">
                                <label>
                                    Add a comment
                                    <textarea
                                        className="muulti-textarea"
                                        value={newCommentText}
                                        onChange={(e) =>
                                            setNewCommentByPost((prev) => ({
                                                ...prev,
                                                [p.id]: e.target.value,
                                            }))
                                        }
                                        placeholder="Write a comment..."
                                    />
                                </label>

                                <label>
                                    Add photos (optional)
                                    <input
                                        className="muulti-input"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) =>
                                            setNewCommentFilesByPost((prev) => ({
                                                ...prev,
                                                [p.id]: Array.from(e.target.files || []),
                                            }))
                                        }
                                    />
                                    <small>Up to {MAX_COMMENT_IMAGES} images.</small>
                                </label>

                                {newCommentFiles.length > 0 && (
                                    <div className="logfeed-comment-media">
                                        {newCommentFiles.map((f, idx) => (
                                            <span key={`${p.id}-new-comment-file-${idx}`}>
                                                {f?.name || "image"}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="muulti-container-button-container">
                                    <button
                                        type="button"
                                        className="muulti-button"
                                        onClick={() => handleCreateComment?.(p.id)}
                                        disabled={!canComment}
                                    >
                                        Add Comment
                                    </button>
                                </div>

                                {!canComment && (
                                    <p className="logfeed-reaction-hint">
                                        Connect wallet, sign wallet authority, and create your MuultiSpace profile to comment.
                                    </p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
            </ComponentAnalyticsTracker>
        </div>
    );
}





// Gate picker that uses EXISTING gateLists (no creation here)
function GatePicker({ value, onChange, donationCampaignOptions = [] }) {
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);

    const logic = value?.logic || "any";
    const gates = Array.isArray(value?.gates) ? value.gates : [{ type: "public" }];

    useEffect(() => {
        let stop = false;
        (async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "gateLists"));
                if (stop) return;
                setLists(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error(e);
                if (!stop) setLists([]);
            } finally {
                if (!stop) setLoading(false);
            }
        })();
        return () => {
            stop = true;
        };
    }, []);

    const setLogic = (next) => onChange?.({ logic: next, gates });

    const setGateAt = (idx, patch) => {
        const next = [...gates];
        next[idx] = { ...(next[idx] || {}), ...patch };
        onChange?.({ logic, gates: next });
    };

    const removeGateAt = (idx) => {
        const next = gates.filter((_, i) => i !== idx);
        onChange?.({ logic, gates: next.length ? next : [{ type: "public" }] });
    };

    const addGate = () => {
        onChange?.({ logic, gates: [...gates, { type: "collection", listId: "" }] });
    };

    // Donation gates are NOT list-backed; they reference an existing donation campaign/component id.
    const gateTypeOptions = ["public", "token", "collection", "compressedCollection", "tokensHeld", "donation"];



    return (
        <div className="component-item" style={{ marginTop: 12 }}>
            <h3>Access (use saved gate lists)</h3>

            <div className="gate-toggle">
                <label>
                    Gate Logic{" "}
                    <select
                        value={logic}
                        onChange={(e) => setLogic(e.target.value === "all" ? "all" : "any")}
                    >
                        <option value="any">Allow if ANY passes</option>
                        <option value="all">Require ALL to pass</option>
                    </select>
                </label>
            </div>

            {loading ? (
                <p>Loading gate lists…</p>
            ) : (
                <>
                    {gates.map((g, idx) => {
                        const t = g?.type || "public";
                        const listId = g?.listId || "";
                        const donationCompId = g?.donationCompId || "";
                        const donationThreshold =
                            typeof g?.donationThreshold === "number" ? g.donationThreshold : 0;
                        const tokenThreshold =
                            typeof g?.tokenThreshold === "number" ? g.tokenThreshold : 0;
                        const heldCurrency = g?.currency || "SOL";

                        const needsList =
                            t === "token" || t === "collection" || t === "compressedCollection";
                        const isDonation = t === "donation";
                        const isTokensHeld = t === "tokensHeld";
                        const filtered = needsList ? lists.filter((l) => l.gateType === t) : [];

                        return (
                            <div key={idx} className="logfeed-gate-row">
                                <div className="logfeed-gate-grid">
                                    <label>
                                        Type
                                        <select
                                            value={t}
                                            onChange={(e) => {
                                                const nextType = e.target.value;
                                                // Reset fields that don't apply.
                                                if (nextType === "public") {
                                                    setGateAt(idx, {
                                                        type: "public",
                                                        listId: "",
                                                        donationCompId: "",
                                                        donationThreshold: 0,
                                                    });
                                                } else if (nextType === "donation") {
                                                    setGateAt(idx, {
                                                        type: "donation",
                                                        listId: "",
                                                        donationCompId: "",
                                                        donationThreshold: 0,
                                                    });
                                                } else if (nextType === "tokensHeld") {
                                                    const token = (DONATION_TOKENS || []).find((opt) => opt.symbol === "SOL");
                                                    setGateAt(idx, {
                                                        type: "tokensHeld",
                                                        listId: "",
                                                        tokenThreshold: 0,
                                                        currency: "SOL",
                                                        tokenMint: token?.mint || "",
                                                    });
                                                } else {
                                                    setGateAt(idx, {
                                                        type: nextType,
                                                        listId: "",
                                                        donationCompId: "",
                                                        donationThreshold: 0,
                                                    });
                                                }
                                            }}
                                        >
                                            {gateTypeOptions.map((x) => (
                                                <option key={x} value={x}>
                                                    {x === "compressedCollection"
                                                        ? "Compressed Collection"
                                                        : x === "tokensHeld"
                                                            ? "Tokens Held"
                                                        : x[0].toUpperCase() + x.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    {/* List-backed gates */}
                                    {needsList && (
                                        <label>
                                            Saved List
                                            <select
                                                value={listId}
                                                onChange={(e) =>
                                                    setGateAt(idx, { listId: e.target.value })
                                                }
                                            >
                                                <option value="">— select —</option>
                                                {filtered.map((l) => (
                                                    <option key={l.id} value={l.id}>
                                                        {l.name || l.id}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    )}

                                    {isTokensHeld && (
                                        <>
                                            <label>
                                                Token
                                                <select
                                                    className="muulti-input"
                                                    value={heldCurrency}
                                                    onChange={(e) => {
                                                        const nextCurrency = e.target.value;
                                                        const token = (DONATION_TOKENS || []).find((opt) => opt.symbol === nextCurrency);
                                                        setGateAt(idx, {
                                                            currency: nextCurrency,
                                                            tokenMint: token?.mint || "",
                                                            tokenThreshold,
                                                            listId: "",
                                                        });
                                                    }}
                                                >
                                                    {(DONATION_TOKENS || []).map((token) => (
                                                        <option key={token.symbol} value={token.symbol}>
                                                            {token.symbol}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label>
                                                Minimum Held ({heldCurrency})
                                                <input
                                                    className="muulti-input"
                                                    type="number"
                                                    min={0}
                                                    step={0.000001}
                                                    value={Number(tokenThreshold || 0)}
                                                    onChange={(e) =>
                                                        setGateAt(idx, { tokenThreshold: Number(e.target.value || 0) })
                                                    }
                                                />
                                            </label>
                                        </>
                                    )}

                                    {/* Donation gates (existing campaign/component id + threshold) */}
                                    {isDonation && (
                                        <>
                                            <label>
                                                Campaign
                                                <select
                                                    className="muulti-input"
                                                    value={String(donationCompId || "")}
                                                    onChange={(e) => setGateAt(idx, { donationCompId: e.target.value })}
                                                >
                                                    <option value="">— select a campaign —</option>
                                                    {donationCampaignOptions.map((opt) => (
                                                        <option key={opt.id} value={opt.id}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>

                                            <label>
                                                Threshold (SOL)
                                                <input
                                                    className="muulti-input"
                                                    type="number"
                                                    min={0}
                                                    step={0.000001}
                                                    value={Number(donationThreshold || 0)}
                                                    onChange={(e) =>
                                                        setGateAt(idx, { donationThreshold: Number(e.target.value || 0) })
                                                    }
                                                />
                                            </label>

                                            {donationCampaignOptions.length === 0 && (
                                                <p style={{ marginTop: 6 }}>
                                                    No donation campaigns found. Create a Donation component on a MuultiSpace page first.
                                                </p>
                                            )}
                                        </>
                                    )}

                                </div>

                                {gates.length > 1 && (
                                    <button
                                        className="muulti-button"
                                        type="button"
                                        onClick={() => removeGateAt(idx)}
                                    >
                                        Remove Gate
                                    </button>
                                )}

                                {needsList && filtered.length === 0 && (
                                    <p style={{ marginTop: 6 }}>
                                        No saved {t} lists found. Create one in Gate Lists first.
                                    </p>
                                )}
                            </div>
                        );
                    })}

                    <div style={{ marginTop: 10 }}>
                        <button className="muulti-button" type="button" onClick={addGate}>
                            + Add another gate
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}



export default function MuultiSpaceLogFeed({
                                              authorUid = "",
                                              hideComposer = false,
                                              embedded = false,
                                              title = "MuultiSpace Social Feed",
                                              subtitle = "Create entries & logs. Newest posts appear first.",
                                              feedTitle = "",
                                              emptyMessageOverride = "",
                                              backLinkPath = "/posts",
                                              backLinkLabel = "← Back to Social Feed",
                                          } = {}) {
    const { postId } = useParams();
    const { pathname } = useLocation();
    const isDetailView = Boolean(postId);
    const scopedAuthorUid = String(authorUid || "").trim();
    const isAuthorScoped = Boolean(scopedAuthorUid);


    // identity
    const [userDoc, setUserDoc] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [userErr, setUserErr] = useState("");
    const [authUid, setAuthUid] = useState(null);
    // form state
    const [content, setContent] = useState("");// ✅ the actual summary text
    const [summaryTouched, setSummaryTouched] = useState(false);  // ✅ whether user edited summary
    const [tagsInput, setTagsInput] = useState("");

    // media upload state (files)
    const [files, setFiles] = useState([]); // File[]
    const [mediaErr, setMediaErr] = useState("");
    const [localFileMedia, setLocalFileMedia] = useState([]);

    // gate state
    const [gateInput, setGateInput] = useState({logic: "any", gates: [{type: "public"}]});

    // submit state
    const [submitting, setSubmitting] = useState(false);
    const [formErr, setFormErr] = useState("");

    // feed state
    const [posts, setPosts] = useState([]);
    const [feedLoading, setFeedLoading] = useState(true);
    const [feedErr, setFeedErr] = useState("");
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [feedMode, setFeedMode] = useState(() => readFeedModePreference());
    const [feedScope, setFeedScope] = useState(() => readFeedScopePreference());
    const [followingPublicKeys, setFollowingPublicKeys] = useState([]);
    const [followingLoading, setFollowingLoading] = useState(false);
    const [followingErr, setFollowingErr] = useState("");
    const [feedSearchInput, setFeedSearchInput] = useState("");
    const [appliedFeedSearchInput, setAppliedFeedSearchInput] = useState("");
    const [feedSearchScopes, setFeedSearchScopes] = useState(() => ({ ...DEFAULT_FEED_SEARCH_SCOPES }));
    const [appliedFeedSearchScopes, setAppliedFeedSearchScopes] = useState(() => ({ ...DEFAULT_FEED_SEARCH_SCOPES }));
    const [currentPage, setCurrentPage] = useState(1);
    const paginationPageCacheRef = useRef({});
    const paginationPageCursorRef = useRef({0: null});
    const loadMoreRef = useRef(null);

    // edit state
    const [editingPostId, setEditingPostId] = useState(null);
    const [editContent, setEditContent] = useState("");
    const [editSummaryOverride, setEditSummaryOverride] = useState("");
    const [editTagsInput, setEditTagsInput] = useState("");
    const [editErr, setEditErr] = useState("");
    const [editLoading, setEditLoading] = useState(false);
    const [composerEditPostId, setComposerEditPostId] = useState(null);
    const [sharingPostId, setSharingPostId] = useState(null);
    const [sharingLinkPostId, setSharingLinkPostId] = useState(null);
    const [linkCopiedPostId, setLinkCopiedPostId] = useState(null);
    const postCardRefs = useRef({});
    const authorProfileRequestsRef = useRef(new Map());
    const backgroundPreviewUrlRef = useRef(null);


    // ===== COMMENTS STATE =====
    const [commentsByPost, setCommentsByPost] = useState({});        // { [postId]: Comment[] }
    const [showCommentsByPost, setShowCommentsByPost] = useState({}); // { [postId]: boolean }
    const [commentsLoadingByPost, setCommentsLoadingByPost] = useState({}); // { [postId]: boolean }
    const [commentErrByPost, setCommentErrByPost] = useState({});     // { [postId]: string }
    const [reactionsByPost, setReactionsByPost] = useState({});       // { [postId]: { counts, byUid } }
    const [reactionsLoadingByPost, setReactionsLoadingByPost] = useState({}); // { [postId]: boolean }
    const [reactionErrByPost, setReactionErrByPost] = useState({});   // { [postId]: string }
    const [authorProfilesByUid, setAuthorProfilesByUid] = useState({}); // { [walletPublicKey]: profile }

// new comment drafts
    const [newCommentByPost, setNewCommentByPost] = useState({});     // { [postId]: string }
    const [newCommentFilesByPost, setNewCommentFilesByPost] = useState({}); // { [postId]: File[] }

// edit comment state
    const [editingCommentByPost, setEditingCommentByPost] = useState({}); // { [postId]: commentId }
    const [editCommentTextByPost, setEditCommentTextByPost] = useState({}); // { [postId]: string }
    const [editCommentErrByPost, setEditCommentErrByPost] = useState({}); // { [postId]: string }
    const [editCommentLoadingByPost, setEditCommentLoadingByPost] = useState({}); // { [postId]: boolean }


    // ✅ desktop sidebar collapse (horizontal)
    const [composerCollapsed, setComposerCollapsed] = useState(false);
    const [summaryOverride, setSummaryOverride] = useState("");
    const [postStyle, setPostStyle] = useState({});
    const [localDraftPost, setLocalDraftPost] = useState(null);
    const draftCreatedAtRef = useRef(null);

    const trimmedContent = (content || "").trim();
    const trimmedSummaryOverride = (summaryOverride || "").trim();
    const [modalImage, setModalImage] = useState(null);

    const [openMenuPostId, setOpenMenuPostId] = useState(null);
    const [openMenuCommentKey, setOpenMenuCommentKey] = useState(null); // `${postId}:${commentId}`

    useEffect(() => {
        try {
            localStorage.setItem(FEED_MODE_STORAGE_KEY, feedMode);
        } catch {}
    }, [feedMode]);

    useEffect(() => {
        try {
            localStorage.setItem(FEED_SCOPE_STORAGE_KEY, feedScope);
        } catch {}
    }, [feedScope]);


    const wallet = useWallet();

    const publicKey = wallet.publicKey;
    const connected = wallet.connected;
    const signMessage = wallet.signMessage;

    const walletStr = useMemo(() => {
        try {
            return publicKey?.toBase58?.() || "";
        } catch {
            return "";
        }
    }, [publicKey]);

    const extractedUrl = extractFirstUrl(trimmedContent);
    const isComposerEditing = Boolean(composerEditPostId);
    const editingComposerPost = useMemo(() => {
        if (!composerEditPostId) return null;
        return posts.find((p) => p.id === composerEditPostId) || null;
    }, [composerEditPostId, posts]);

    useEffect(() => {
        if (!files.length) {
            setLocalFileMedia([]);
            return;
        }

        const nextMedia = files.map((file) => ({
            url: URL.createObjectURL(file),
            mimeType: file.type || "",
            name: file.name || "",
        }));

        setLocalFileMedia(nextMedia);

        return () => {
            nextMedia.forEach((media) => {
                if (media?.url) URL.revokeObjectURL(media.url);
            });
        };
    }, [files]);

    useEffect(() => {
        return () => {
            if (backgroundPreviewUrlRef.current) {
                URL.revokeObjectURL(backgroundPreviewUrlRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!openMenuPostId) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") setOpenMenuPostId(null);
        };

        const onMouseDown = (e) => {
            // close if click is outside the active menu
            const el = e.target;
            const insideMenu = el?.closest?.(`[data-post-menu-id="${openMenuPostId}"]`);
            if (!insideMenu) setOpenMenuPostId(null);
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onMouseDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onMouseDown);
        };
    }, [openMenuPostId]);


    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setAuthUid(u?.uid || null);
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!connected || !walletStr) return;

        // If already deterministic, do nothing. Otherwise, upgrade auth.
        if (auth.currentUser?.uid === walletStr) return;

        requireDeterministicAuth().catch(() => {
            // swallow: they can still browse, and auth can be upgraded when they take an action
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, walletStr]);

    // Load user doc by publicKey → must have muultiName
    useEffect(() => {
        (async () => {
            setUserErr("");
            setUserLoading(true);
            setUserDoc(null);

            if (!connected || !walletStr) {
                setUserLoading(false);
                return;
            }

            try {
                const usersRef = collection(db, "users");
                const q = query(usersRef, where("publicKey", "==", walletStr));
                const snap = await getDocs(q);

                if (snap.empty) setUserDoc(null);
                else {
                    const d = snap.docs[0];
                    setUserDoc({id: d.id, ...d.data()});
                }
            } catch (e) {
                console.error(e);
                setUserErr("Failed to load your profile.");
            } finally {
                setUserLoading(false);
            }
        })();
    }, [connected, walletStr]);

    useEffect(() => {
        let stop = false;

        const loadFollowing = async () => {
            if (isAuthorScoped || isDetailView || feedScope !== "following") {
                setFollowingPublicKeys([]);
                setFollowingErr("");
                setFollowingLoading(false);
                return;
            }

            if (!walletStr) {
                setFollowingPublicKeys([]);
                setFollowingErr("");
                setFollowingLoading(false);
                return;
            }

            setFollowingLoading(true);
            setFollowingErr("");
            try {
                const keys = await loadFollowingPublicKeys(walletStr);
                if (stop) return;
                setFollowingPublicKeys(keys);
            } catch (err) {
                console.error("Following feed load failed:", err);
                if (!stop) {
                    setFollowingPublicKeys([]);
                    setFollowingErr("Could not load followed spaces.");
                }
            } finally {
                if (!stop) setFollowingLoading(false);
            }
        };

        loadFollowing();
        return () => {
            stop = true;
        };
    }, [feedScope, isAuthorScoped, isDetailView, walletStr]);

    useEffect(() => {
        // If a donation gate is selected
        if (!hasDonationGate(gateInput)) return;

        // Only auto-fill if the user has not typed a summary
        if (summaryTouched) return;

        // Only auto-fill if summary is currently empty
        if ((summaryOverride || "").trim()) return;

        setSummaryOverride(SECRET_SUMMARY_DEFAULT);
    }, [gateInput, summaryTouched, summaryOverride]);

    useEffect(() => {
        if (!openMenuCommentKey) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") setOpenMenuCommentKey(null);
        };

        const onMouseDown = (e) => {
            const el = e.target;
            const insideMenu = el?.closest?.(`[data-comment-menu-key="${openMenuCommentKey}"]`);
            if (!insideMenu) setOpenMenuCommentKey(null);
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("mousedown", onMouseDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("mousedown", onMouseDown);
        };
    }, [openMenuCommentKey]);

    const effectiveUid = (connected && walletStr) ? walletStr : authUid;

    const isOwnerOfPost = (p) => {
        if (!authUid) return false;
        return p?.authorUid === authUid;
    };

    const isOwnerOfComment = (c) => {
        if (!connected || !walletStr) return false; // must have a wallet identity
        return c?.authorUid === authUid;         // comments use wallet pubkey as authorUid
    };


    const getDeterministicUid = async () => {
        if (!connected || !walletStr) throw new Error("Wallet not connected.");

        // ✅ MUST be first: ensures request.auth.uid === wallet pubkey
        await requireDeterministicAuth();

        // wait one tick for auth state to settle (same pattern as MuultiPage)
        if (auth?.currentUser?.uid !== walletStr) {
            await new Promise((r) => setTimeout(r, 0));
        }

        if (auth?.currentUser?.uid !== walletStr) {
            throw new Error(`Auth UID mismatch: ${auth?.currentUser?.uid} !== ${walletStr}`);
        }

        return walletStr;
    };

    const requireDeterministicAuth = useCallback(async () => {
        if (!connected || !publicKey) throw new Error("Wallet not connected");

        const walletUid = publicKey.toBase58();

        // already correct identity
        if (auth?.currentUser?.uid === walletUid) return walletUid;

        await openPublishSessionForWallet(wallet);

        if (auth?.currentUser?.uid !== walletUid) {
            throw new Error("Auth UID does not match wallet public key after publish session.");
        }

        return walletUid;
    }, [connected, publicKey, signMessage, wallet?.adapter]);


    const beginEditPost = (p) => {
        setEditErr("");
        setEditingPostId(p.id);
        setEditContent(p?.object?.content || "");
        setEditSummaryOverride(p?.object?.summary || "");
        setEditTagsInput(Array.isArray(p?.object?.tags) ? p.object.tags.join(", ") : "");
    };

    const cancelEditPost = () => {
        setEditErr("");
        setEditingPostId(null);
        setEditContent("");
        setEditSummaryOverride("");
        setEditTagsInput("");
    };

    const resetComposerForm = () => {
        setContent("");
        setSummaryOverride("");
        setSummaryTouched(false);
        setTagsInput("");
        setFiles([]);
        setGateInput({logic: "any", gates: [{type: "public"}]});
        setPostStyle({});
        setLocalDraftPost(null);
        draftCreatedAtRef.current = null;
    };

    const beginComposerEditPost = (p) => {
        setFormErr("");
        setMediaErr("");
        setComposerEditPostId(p.id);
        setContent(p?.object?.content || "");
        setSummaryOverride(p?.object?.summary || "");
        setSummaryTouched(Boolean((p?.object?.summary || "").trim()));
        setTagsInput(Array.isArray(p?.object?.tags) ? p.object.tags.join(", ") : "");
        setFiles([]);
        setGateInput(p?.gate ? normalizeGateForPost(p.gate) : {logic: "any", gates: [{type: "public"}]});
        setPostStyle(p?.style || {});
        setLocalDraftPost(null);
        draftCreatedAtRef.current = null;
        setComposerCollapsed(false);
    };

    const cancelComposerEdit = () => {
        setComposerEditPostId(null);
        setFormErr("");
        setMediaErr("");
        resetComposerForm();
    };

    useEffect(() => {
        if (isDetailView) return;
        const shareDraft = consumeSocialFeedShareDraft();
        if (!shareDraft) return;

        setComposerEditPostId(null);
        setFormErr("");
        setMediaErr("");
        setContent(String(shareDraft.content || ""));
        setSummaryOverride(String(shareDraft.summary || ""));
        setSummaryTouched(Boolean(String(shareDraft.summary || "").trim()));
        setTagsInput(Array.isArray(shareDraft.tags) ? shareDraft.tags.join(", ") : "");
        setFiles([]);
        setLocalFileMedia([]);
        setGateInput(
            shareDraft.gateInput
                ? normalizeGateForPost(shareDraft.gateInput)
                : {logic: "any", gates: [{type: "public"}]}
        );
        setPostStyle({});
        setLocalDraftPost(null);
        draftCreatedAtRef.current = null;
        setComposerCollapsed(false);
    }, [isDetailView]);

    const generatePostCardImage = async (post) => {
        const cardEl = postCardRefs.current[post?.id];
        if (!cardEl) return null;

        const safeAuthor = (post?.authorMuultiName || "post").replace(/[^\w\-]+/g, "_");
        const safeId = String(post?.id || "post").slice(0, 10);
        const fileName = `${safeAuthor}-${safeId}.png`;

        const bg = window.getComputedStyle(cardEl).backgroundColor || null;

        const blob = await htmlToImage.toBlob(cardEl, {
            backgroundColor: bg,
            pixelRatio: 2,
            cacheBust: true,
            useCORS: true,
        });

        if (!blob) return null;
        return { blob, fileName };
    };

    const handleDownloadPostCard = async (post) => {
        try {
            setSharingPostId(post?.id || "working");
            const result = await generatePostCardImage(post);
            if (!result) return;

            const { blob, fileName } = result;
            const urlObj = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlObj;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(urlObj);
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setSharingPostId(null);
        }
    };

    const handleCopyPostCard = async (post) => {
        try {
            setSharingPostId(post?.id || "working");
            const result = await generatePostCardImage(post);
            if (!result) return;

            const { blob } = result;

            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
            } else {
                alert("Copying images is not supported on this browser/device.");
            }
        } catch (err) {
            console.error("Copy failed:", err);
            alert("Copy not supported on this device/browser.");
        } finally {
            setSharingPostId(null);
        }
    };


    // Share one profile read per wallet across posts, comments, and account search.
    const loadAuthorProfiles = useCallback(async (entries) => {
        const authorUids = Array.from(new Set(
            entries.map((entry) => String(entry?.authorUid || "").trim()).filter(Boolean)
        ));
        const pairs = await Promise.all(authorUids.map(async (uid) => {
            if (!authorProfileRequestsRef.current.has(uid)) {
                const request = getDocs(query(
                    collection(db, "users"),
                    where("publicKey", "==", uid),
                    limit(1)
                )).then((snap) => {
                    if (snap.empty) return null;
                    const data = snap.docs[0].data();
                    return {
                        muultiName: String(data.muultiName || "").trim(),
                        profilePicUrl: String(data.profilePicUrl || ""),
                        showFollowerCount: data.showFollowerCount === true,
                    };
                })
                    .catch((err) => {
                        console.error("Failed to load feed author profile:", err);
                        return null;
                    });
                authorProfileRequestsRef.current.set(uid, request);
                request.then((profile) => {
                    setAuthorProfilesByUid((prev) => ({ ...prev, [uid]: profile }));
                });
            }
            return [uid, await authorProfileRequestsRef.current.get(uid)];
        }));
        return Object.fromEntries(pairs);
    }, []);

    // Feed loading
    const feedSearchTerms = useMemo(
        () => parseSearchInput(appliedFeedSearchInput, FEED_SEARCH_MAX_TERMS),
        [appliedFeedSearchInput]
    );
    const normalizedAppliedSearchScopes = useMemo(
        () => normalizeSearchScopes(appliedFeedSearchScopes),
        [appliedFeedSearchScopes]
    );
    const feedSearchTermKey = feedSearchTerms.join("|");
    const feedSearchScopeKey = [
        normalizedAppliedSearchScopes.keywords ? "keywords" : "",
        normalizedAppliedSearchScopes.tags ? "tags" : "",
        normalizedAppliedSearchScopes.accounts ? "accounts" : "",
    ].filter(Boolean).join("|");
    const isFeedSearchActive = feedSearchTerms.length > 0 && hasSelectedSearchScope(normalizedAppliedSearchScopes);
    const appliedFeedSearchLabel = feedSearchTerms.join(", ");
    const appliedFeedSearchScopeLabel = getSearchScopeLabel(normalizedAppliedSearchScopes);
    const followingPublicKeySet = useMemo(
        () => new Set((followingPublicKeys || []).map((key) => String(key || "").trim()).filter(Boolean)),
        [followingPublicKeys]
    );
    const followingPublicKeyKey = useMemo(
        () => Array.from(followingPublicKeySet).sort().join("|"),
        [followingPublicKeySet]
    );
    const isFollowingFeedActive = !isAuthorScoped && !isDetailView && feedScope === "following";

    const postMatchesFeedScope = useCallback((post) => {
        if (!isFollowingFeedActive) return true;
        return followingPublicKeySet.has(String(post?.authorUid || "").trim());
    }, [followingPublicKeySet, isFollowingFeedActive]);

    const resetPaginationCache = () => {
        paginationPageCacheRef.current = {};
        paginationPageCursorRef.current = {0: null};
        setCurrentPage(1);
    };

    const applyPaginationPage = (pageNumber, entry) => {
        setPosts(Array.isArray(entry?.posts) ? entry.posts : []);
        setCursor(entry?.cursor || null);
        setHasMore(Boolean(entry?.hasMore));
        setCurrentPage(pageNumber);
    };

    const loadSearchPage = async (startCursor = null) => {
        if (isFollowingFeedActive && followingPublicKeySet.size === 0) {
            return { posts: [], cursor: null, hasMore: false };
        }

        const postsRef = collection(db, "posts");
        const scopeConstraints = scopedAuthorUid ? [where("authorUid", "==", scopedAuthorUid)] : [];
        const matches = [];
        let scanCursor = startCursor || null;
        let lastScannedDoc = startCursor || null;
        let scannedCount = 0;
        let reachedEnd = false;

        while (matches.length < PAGE_SIZE && scannedCount < SEARCH_SCAN_MAX_DOCS) {
            const q = scanCursor
                ? query(
                    postsRef,
                    ...scopeConstraints,
                    orderBy("createdAt", "desc"),
                    startAfter(scanCursor),
                    limit(SEARCH_SCAN_BATCH_SIZE)
                )
                : query(
                    postsRef,
                    ...scopeConstraints,
                    orderBy("createdAt", "desc"),
                    limit(SEARCH_SCAN_BATCH_SIZE)
                );

            const snap = await getDocs(q);
            if (snap.empty) {
                reachedEnd = true;
                break;
            }

            scannedCount += snap.docs.length;
            lastScannedDoc = snap.docs[snap.docs.length - 1] || lastScannedDoc;
            scanCursor = lastScannedDoc;

            const batch = snap.docs.map((d) => ({id: d.id, ...d.data()}))
                .filter((post) => postIsVisibleToViewer(post, authUid) && postMatchesFeedScope(post));
            const profiles = isFeedSearchActive && normalizedAppliedSearchScopes.accounts
                ? await loadAuthorProfiles(batch)
                : {};

            batch.forEach((post) => {
                if (matches.length >= PAGE_SIZE) return;
                const currentPost = withCurrentAuthorName(post, profiles);
                if (!postMatchesFeedSearch(currentPost, feedSearchTerms, normalizedAppliedSearchScopes)) return;
                matches.push(post);
            });

            if (snap.docs.length < SEARCH_SCAN_BATCH_SIZE) {
                reachedEnd = true;
                break;
            }
        }

        return {
            posts: matches,
            cursor: lastScannedDoc,
            hasMore: Boolean(lastScannedDoc) && !reachedEnd,
        };
    };

    const loadSinglePost = async (id) => {
        setFeedErr("");
        setFeedLoading(true);
        setPosts([]);
        setCursor(null);
        setHasMore(false);
        resetPaginationCache();

        try {
            const snap = await getDoc(doc(db, "posts", String(id)));
            if (snap.exists()) {
                setPosts([{ id: snap.id, ...snap.data() }]);
            } else {
                setPosts([]);
            }
        } catch (e) {
            console.error(e);
            setFeedErr("Failed to load post.");
        } finally {
            setFeedLoading(false);
        }
    };

    const loadFeed = async ({reset = false, page = 1} = {}) => {
        setFeedErr("");
        setFeedLoading(true);

        if (feedMode === "pagination") {
            const targetPage = Math.max(1, Number(page) || 1);

            if (reset) {
                setPosts([]);
                setCursor(null);
                setHasMore(true);
                resetPaginationCache();
            }

            try {
                const cachedPage = paginationPageCacheRef.current[targetPage];
                if (cachedPage && !reset) {
                    applyPaginationPage(targetPage, cachedPage);
                    return;
                }

                const previousPageCursor =
                    targetPage > 1 ? paginationPageCursorRef.current[targetPage - 1] : null;
                if (targetPage > 1 && !previousPageCursor) {
                    throw new Error("Missing cursor for requested page.");
                }

                let nextPageEntry;
                if (isFeedSearchActive || isFollowingFeedActive) {
                    nextPageEntry = await loadSearchPage(previousPageCursor);
                } else {
                    const postsRef = collection(db, "posts");
                    const base = [
                        ...(scopedAuthorUid ? [where("authorUid", "==", scopedAuthorUid)] : []),
                        orderBy("createdAt", "desc"),
                        limit(PAGE_SIZE),
                    ];
                    const q = previousPageCursor
                        ? query(postsRef, ...base, startAfter(previousPageCursor))
                        : query(postsRef, ...base);

                    const snap = await getDocs(q);
                    const batch = snap.docs.map((d) => ({id: d.id, ...d.data()}));
                    const last = snap.docs[snap.docs.length - 1] || null;
                    const pageHasMore = snap.docs.length === PAGE_SIZE;

                    nextPageEntry = {
                        posts: batch,
                        cursor: last,
                        hasMore: pageHasMore,
                    };
                }

                paginationPageCacheRef.current[targetPage] = nextPageEntry;
                paginationPageCursorRef.current[targetPage] = nextPageEntry.cursor;
                applyPaginationPage(targetPage, nextPageEntry);
            } catch (e) {
                console.error(e);
                setFeedErr("Failed to load feed.");
            } finally {
                setFeedLoading(false);
            }
            return;
        }

        if (reset) {
            setPosts([]);
            setCursor(null);
            setHasMore(true);
            setCurrentPage(1);
            resetPaginationCache();
        }

        try {
            if (isFeedSearchActive || isFollowingFeedActive) {
                const entry = await loadSearchPage(reset ? null : cursor);
                setPosts((prev) => (reset ? entry.posts : [...prev, ...entry.posts]));
                setCursor(entry.cursor);
                setHasMore(entry.hasMore);
            } else {
                const postsRef = collection(db, "posts");
                const base = [
                    ...(scopedAuthorUid ? [where("authorUid", "==", scopedAuthorUid)] : []),
                    orderBy("createdAt", "desc"),
                    limit(PAGE_SIZE),
                ];
                const q =
                    reset || !cursor ? query(postsRef, ...base) : query(postsRef, ...base, startAfter(cursor));

                const snap = await getDocs(q);
                const batch = snap.docs.map((d) => ({id: d.id, ...d.data()}));

                setPosts((prev) => (reset ? batch : [...prev, ...batch]));

                const last = snap.docs[snap.docs.length - 1] || null;
                setCursor(last);
                setHasMore(snap.docs.length === PAGE_SIZE);
            }
        } catch (e) {
            console.error(e);
            setFeedErr("Failed to load feed.");
        } finally {
            setFeedLoading(false);
        }
    };

    useEffect(() => {
        if (postId) {
            loadSinglePost(postId);
        } else {
            loadFeed({reset: true, page: 1});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postId, feedMode, feedSearchTermKey, feedSearchScopeKey, authUid, scopedAuthorUid, feedScope, followingPublicKeyKey]);

    useEffect(() => {
        if (isDetailView) return;
        if (feedMode !== "infinite") return;
        const node = loadMoreRef.current;
        if (!node) return;
        if (!("IntersectionObserver" in window)) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting) return;
                if (feedLoading || !hasMore) return;
                loadFeed({ reset: false });
            },
            { rootMargin: "200px" }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [feedLoading, hasMore, isDetailView, feedMode, loadFeed]);

    const supportsIO = typeof window !== "undefined" && "IntersectionObserver" in window;
    const hasCachedNextPage = Boolean(paginationPageCacheRef.current[currentPage + 1]);
    const canGoPrevPage = currentPage > 1;
    const canGoNextPage = hasCachedNextPage || hasMore;

    const goToPrevPage = async () => {
        if (feedLoading || currentPage <= 1) return;
        const targetPage = currentPage - 1;
        const cachedPage = paginationPageCacheRef.current[targetPage];
        if (cachedPage) {
            applyPaginationPage(targetPage, cachedPage);
            return;
        }
        await loadFeed({reset: false, page: targetPage});
    };

    const goToNextPage = async () => {
        if (feedLoading) return;
        const targetPage = currentPage + 1;
        const cachedPage = paginationPageCacheRef.current[targetPage];
        if (cachedPage) {
            applyPaginationPage(targetPage, cachedPage);
            return;
        }
        if (!hasMore) return;
        await loadFeed({reset: false, page: targetPage});
    };

    const handleFeedModeChange = (nextMode) => {
        const normalized = nextMode === "infinite" ? "infinite" : "pagination";
        setFeedMode((prev) => (prev === normalized ? prev : normalized));
    };

    const handleFeedScopeChange = (nextScope) => {
        const normalized = nextScope === "following" ? "following" : "public";
        setFeedScope((prev) => (prev === normalized ? prev : normalized));
    };

    const applyFeedSearch = useCallback((input, scopes = feedSearchScopes) => {
        const nextTerms = parseSearchInput(input, FEED_SEARCH_MAX_TERMS);
        const nextValue = nextTerms.join(" ");
        const nextScopes = normalizeSearchScopes(scopes);
        setFeedSearchInput(nextValue);
        setFeedSearchScopes(nextScopes);
        setAppliedFeedSearchInput(nextValue);
        setAppliedFeedSearchScopes(nextScopes);
    }, [feedSearchScopes]);

    const handleFeedSearchSubmit = useCallback((e) => {
        e.preventDefault();
        applyFeedSearch(feedSearchInput, feedSearchScopes);
    }, [applyFeedSearch, feedSearchInput, feedSearchScopes]);

    const handleClearFeedSearch = useCallback(() => {
        setFeedSearchInput("");
        setAppliedFeedSearchInput("");
    }, []);

    const toggleFeedSearchScope = useCallback((scope) => {
        setFeedSearchScopes((prev) => {
            const next = { ...prev, [scope]: !prev?.[scope] };
            return hasSelectedSearchScope(next) ? next : prev;
        });
    }, []);

    const handleSearchTagClick = useCallback((tag) => {
        applyFeedSearch(tag, {keywords: false, tags: true, accounts: false});
    }, [applyFeedSearch]);

    const visiblePosts = useMemo(() => {
        return posts.filter((p) => p?.status !== "draft" || p?.authorUid === authUid);
    }, [posts, authUid]);

    const getPostDetailPath = useCallback((post) => {
        const id = String(post?.id || "").trim();
        if (!id) return "";
        return `/posts/${id}`;
    }, []);

    const getPostDetailUrl = useCallback((post) => {
        const path = getPostDetailPath(post);
        if (!path) return "";
        if (typeof window === "undefined") return path;
        return `${window.location.origin}${path}`;
    }, [getPostDetailPath]);

    const handleSharePostLink = async (post) => {
        const url = getPostDetailUrl(post);
        if (!url) return;
        const shareTitle = post?.authorMuultiName
            ? `${post.authorMuultiName} on MuultiSpace`
            : "MuultiSpace post";
        const shareText = makePreviewText({
            content: post?.object?.content || "",
            summary: post?.object?.summary || "",
        });

        setSharingLinkPostId(post?.id || "working");
        try {
            if (navigator.share) {
                await navigator.share({ title: shareTitle, text: shareText, url });
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                setLinkCopiedPostId(post?.id || null);
                setTimeout(() => setLinkCopiedPostId(null), 1600);
            } else {
                window.prompt("Copy link:", url);
            }
        } catch (err) {
            console.error("Share link failed:", err);
        } finally {
            setSharingLinkPostId(null);
        }
    };

    const canPost = useMemo(() => {
        if (!connected || !walletStr) return false;
        if (!userDoc?.muultiName) return false;
        return true;
    }, [connected, walletStr, userDoc]);

    const canComment = useMemo(() => {
        if (!connected || !walletStr) return false;
        if (!userDoc?.muultiName) return false; // “has a space”
        return true;
    }, [connected, walletStr, userDoc]);

    const canReact = useMemo(() => {
        if (!connected || !walletStr) return false;
        if (!userDoc?.muultiName) return false;
        return true;
    }, [connected, walletStr, userDoc]);

    const parsedTags = useMemo(() => {
        return parseTagsInput(tagsInput);
    }, [tagsInput]);

    const localEditPost = useMemo(() => {
        if (!isComposerEditing || !editingComposerPost) return null;

        const normalizedStyle = normalizePostStyle(postStyle);
        const finalSummary = trimmedSummaryOverride || clampSummaryFromContent(trimmedContent, 100);
        const nextMedia = files.length ? localFileMedia : (editingComposerPost.object?.media || []);
        const mediaKind = files.length ? detectMediaKind(files) : "none";
        const nextObject = {
            ...(editingComposerPost.object || {}),
            content: trimmedContent,
            summary: finalSummary || "",
            media: nextMedia,
            tags: parsedTags,
            url: extractedUrl || "",
        };
        const nextPreview = {
            ...(editingComposerPost.preview || {}),
            text: makePreviewText({content: trimmedContent, summary: finalSummary}),
        };
        if (files.length) {
            nextPreview.imageUrl =
                mediaKind === "image" && nextMedia?.[0]?.url ? nextMedia[0].url : "";
        }

        return {
            ...editingComposerPost,
            object: nextObject,
            preview: nextPreview,
            gate: normalizeGateForPost(gateInput),
            style: normalizedStyle || undefined,
        };
    }, [
        isComposerEditing,
        editingComposerPost,
        trimmedContent,
        trimmedSummaryOverride,
        parsedTags,
        extractedUrl,
        gateInput,
        postStyle,
        files,
        localFileMedia,
    ]);

    const displayPosts = useMemo(() => {
        let nextPosts = visiblePosts;
        if (localEditPost) {
            nextPosts = nextPosts.map((p) => (p.id === localEditPost.id ? localEditPost : p));
        }
        const visibleLocalDraftPost = localDraftPost && (
            !isFeedSearchActive ||
            postMatchesFeedSearch(localDraftPost, feedSearchTerms, normalizedAppliedSearchScopes)
        )
            ? localDraftPost
            : null;
        if (!visibleLocalDraftPost) {
            if (postId) {
                return nextPosts.filter((p) => String(p.id) === String(postId));
            }
            return nextPosts;
        }
        const withoutDraft = nextPosts.filter((p) => p.id !== visibleLocalDraftPost.id);
        const merged = [visibleLocalDraftPost, ...withoutDraft];
        if (postId) {
            return merged.filter((p) => String(p.id) === String(postId));
        }
        return merged;
    }, [
        visiblePosts,
        localDraftPost,
        localEditPost,
        postId,
        isFeedSearchActive,
        feedSearchTerms,
        normalizedAppliedSearchScopes,
    ]);

    useEffect(() => {
        loadAuthorProfiles([...displayPosts, ...Object.values(commentsByPost).flat()]);
    }, [displayPosts, commentsByPost, loadAuthorProfiles]);

    const resolvedDisplayPosts = useMemo(
        () => displayPosts.map((post) => withCurrentAuthorName(post, authorProfilesByUid)),
        [displayPosts, authorProfilesByUid]
    );
    const resolvedCommentsByPost = useMemo(() => Object.fromEntries(
        Object.entries(commentsByPost).map(([id, comments]) => [
            id, comments.map((comment) => withCurrentAuthorName(comment, authorProfilesByUid)),
        ])
    ), [commentsByPost, authorProfilesByUid]);

    const handleGateInputChange = useCallback((next) => {
        setGateInput(next);
    }, []);

    // list of the creator's donation components to pick from (same pattern as MuultiPage)
    const donationCampaignOptions = useMemo(() => {
        const comps = Array.isArray(userDoc?.components) ? userDoc.components : [];
        return comps
            .filter((c) => c?.type === "donation" && c?.id)
            .map((c) => ({
                id: String(c.id),
                label: c.campaignName || `Campaign #${c.id}`,
            }));
    }, [userDoc]);

    const donationLookup = useMemo(() => {
        const comps = Array.isArray(userDoc?.components) ? userDoc.components : [];
        const map = new Map();
        for (const c of comps) {
            if (c?.type !== "donation" || !c?.id) continue;
            map.set(String(c.id), {
                campaignName: c?.campaignName || "",
            });
        }
        return map;
    }, [userDoc]);

    // validate + set selected files
    const onFilesSelected = (fileList) => {
        setMediaErr("");

        const next = Array.from(fileList || []);
        if (!next.length) {
            setFiles([]);
            return;
        }

        const kind = detectMediaKind(next);
        if (kind === "mixed" || kind === "other") {
            setFiles([]);
            setMediaErr("Please upload only one media type at a time (images OR audio OR video).");
            return;
        }

        if (kind === "image") {
            if (next.length > MAX_IMAGES) {
                setFiles([]);
                setMediaErr(`Max ${MAX_IMAGES} images per post.`);
                return;
            }
            for (const f of next) {
                if (bytesToMB(f.size) > MAX_IMAGE_MB) {
                    setFiles([]);
                    setMediaErr(`Each image must be ≤ ${MAX_IMAGE_MB}MB.`);
                    return;
                }
            }
            setFiles(next);
            return;
        }

        if (next.length > 1) {
            setFiles([]);
            setMediaErr(`Only 1 ${kind} file allowed per post.`);
            return;
        }

        const f = next[0];
        const mb = bytesToMB(f.size);

        if (kind === "audio" && mb > MAX_AUDIO_MB) {
            setFiles([]);
            setMediaErr(`Audio must be ≤ ${MAX_AUDIO_MB}MB.`);
            return;
        }
        if (kind === "video" && mb > MAX_VIDEO_MB) {
            setFiles([]);
            setMediaErr(`Video must be ≤ ${MAX_VIDEO_MB}MB.`);
            return;
        }

        setFiles(next);
    };


    const loadCommentsForPost = async (postId) => {
        setCommentErrByPost((prev) => ({...prev, [postId]: ""}));
        setCommentsLoadingByPost((prev) => ({...prev, [postId]: true}));

        try {
            const commentsRef = collection(db, "posts", postId, "comments");
            const q = query(commentsRef, orderBy("createdAt", "asc"), limit(200));
            const snap = await getDocs(q);
            const items = snap.docs.map((d) => ({id: d.id, ...d.data()}));

            setCommentsByPost((prev) => ({...prev, [postId]: items}));
        } catch (e) {
            console.error(e);
            setCommentErrByPost((prev) => ({...prev, [postId]: "Failed to load comments."}));
        } finally {
            setCommentsLoadingByPost((prev) => ({...prev, [postId]: false}));
        }
    };

    const loadReactionsForPost = async (postId) => {
        if (!postId || postId === "local-draft") return;

        setReactionErrByPost((prev) => ({ ...prev, [postId]: "" }));
        setReactionsLoadingByPost((prev) => ({ ...prev, [postId]: true }));

        try {
            const reactionsRef = collection(db, "posts", postId, "reactions");
            const snap = await getDocs(query(reactionsRef, limit(500)));

            const counts = emptyReactionCounts();
            const byUid = {};

            snap.forEach((d) => {
                const data = d.data() || {};
                const type = normalizeReactionType(data?.type);
                if (!type) return;

                const uid = String(data?.authorUid || d.id || "").trim();
                counts[type] = (counts[type] || 0) + 1;
                if (uid) byUid[uid] = { id: d.id, type, ...data };
            });

            setReactionsByPost((prev) => ({ ...prev, [postId]: { counts, byUid } }));
        } catch (e) {
            console.error(e);
            setReactionErrByPost((prev) => ({ ...prev, [postId]: "Failed to load reactions." }));
        } finally {
            setReactionsLoadingByPost((prev) => ({ ...prev, [postId]: false }));
        }
    };

    const handleSetReaction = async (postId, reactionType) => {
        if (!postId || postId === "local-draft") return;

        const normalizedType = normalizeReactionType(reactionType);
        if (!normalizedType) {
            setReactionErrByPost((prev) => ({ ...prev, [postId]: "Invalid reaction type." }));
            return;
        }

        setReactionErrByPost((prev) => ({ ...prev, [postId]: "" }));

        if (!canReact) {
            setReactionErrByPost((prev) => ({
                ...prev,
                [postId]: "Connect wallet, sign wallet authority, and create your MuultiSpace profile first.",
            }));
            return;
        }

        try {
            const uid = await requireDeterministicAuth();
            const reactionRef = doc(db, "posts", postId, "reactions", uid);
            const existingSnap = await getDoc(reactionRef);
            const existingType = normalizeReactionType(existingSnap.data()?.type);

            if (existingSnap.exists() && existingType === normalizedType) {
                await deleteDoc(reactionRef);
            } else if (existingSnap.exists()) {
                await updateDoc(reactionRef, {
                    type: normalizedType,
                    authorUid: uid,
                    authorMuultiName: userDoc?.muultiName || "",
                    updatedAt: serverTimestamp(),
                });
            } else {
                await setDoc(reactionRef, {
                    postId,
                    authorUid: uid,
                    authorMuultiName: userDoc?.muultiName || "",
                    type: normalizedType,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            }

            await loadReactionsForPost(postId);
        } catch (e) {
            console.error(e);
            setReactionErrByPost((prev) => ({ ...prev, [postId]: e?.message || "Failed to update reaction." }));
        }
    };

    const handleCreateComment = async (postId) => {
        setCommentErrByPost((prev) => ({...prev, [postId]: ""}));

        if (!canComment) {
            setCommentErrByPost((prev) => ({...prev, [postId]: "Connect your wallet and create your space first."}));
            return;
        }

        const text = (newCommentByPost?.[postId] || "").trim();
        const files = newCommentFilesByPost?.[postId] || [];

        if (!text && !files.length) {
            setCommentErrByPost((prev) => ({...prev, [postId]: "Write a comment or add a photo."}));
            return;
        }

        try {
            const uid = await requireDeterministicAuth(); // ✅ ensures auth.uid === wallet pubkey
            const authorMuultiName = userDoc.muultiName;

            const media = await uploadCommentImages(uid, postId, files);

            await addDoc(collection(db, "posts", postId, "comments"), {
                postId,
                authorUid: uid,
                authorMuultiName,
                content: text,
                media,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            // clear draft
            setNewCommentByPost((prev) => ({...prev, [postId]: ""}));
            setNewCommentFilesByPost((prev) => ({...prev, [postId]: []}));

            // refresh this post’s comments
            await loadCommentsForPost(postId);
        } catch (e) {
            console.error(e);
            setCommentErrByPost((prev) => ({...prev, [postId]: e?.message || "Failed to add comment."}));
        }
    };

    const beginEditComment = (postId, comment) => {
        setEditCommentErrByPost((prev) => ({...prev, [postId]: ""}));
        setEditingCommentByPost((prev) => ({...prev, [postId]: comment.id}));
        setEditCommentTextByPost((prev) => ({...prev, [postId]: comment.content || ""}));
    };

    const cancelEditComment = (postId) => {
        setEditCommentErrByPost((prev) => ({...prev, [postId]: ""}));
        setEditingCommentByPost((prev) => ({...prev, [postId]: null}));
        setEditCommentTextByPost((prev) => ({...prev, [postId]: ""}));
    };




    const handleSaveEditComment = async (postId) => {
        const commentId = editingCommentByPost?.[postId];
        if (!commentId) return;

        setEditCommentErrByPost((prev) => ({...prev, [postId]: ""}));
        setEditCommentLoadingByPost((prev) => ({...prev, [postId]: true}));

        try {
            const uid = await requireDeterministicAuth();

            const currentComments = commentsByPost?.[postId] || [];
            const existing = currentComments.find((c) => c.id === commentId);
            if (!existing) throw new Error("Comment not found.");
            if (existing.authorUid !== uid) throw new Error("You can only edit your own comments.");

            const nextText = (editCommentTextByPost?.[postId] || "").trim();
            if (!nextText) throw new Error("Comment cannot be empty.");

            await updateDoc(doc(db, "posts", postId, "comments", commentId), {
                content: nextText,
                updatedAt: serverTimestamp(),
            });

            cancelEditComment(postId);
            await loadCommentsForPost(postId);
        } catch (e) {
            console.error(e);
            setEditCommentErrByPost((prev) => ({...prev, [postId]: e?.message || "Failed to update comment."}));
        } finally {
            setEditCommentLoadingByPost((prev) => ({...prev, [postId]: false}));
        }
    };

    const handleDeleteComment = async (postId, commentId) => {
        const ok = window.confirm("Delete this comment? This cannot be undone.");
        if (!ok) return;

        try {
            const uid = await requireDeterministicAuth();

            const currentComments = commentsByPost?.[postId] || [];
            const existing = currentComments.find((c) => c.id === commentId);
            if (!existing) throw new Error("Comment not found.");
            if (existing.authorUid !== uid) throw new Error("You can only delete your own comments.");

            await deleteDoc(doc(db, "posts", postId, "comments", commentId));
            await loadCommentsForPost(postId);
        } catch (e) {
            console.error(e);
            setCommentErrByPost((prev) => ({...prev, [postId]: e?.message || "Failed to delete comment."}));
        }
    };


    // Upload files to Storage: posts/{uid}/...
    const uploadMediaFiles = async (uid, inputFiles = files) => {
        if (!inputFiles.length) return [];

        const uploads = inputFiles.map((file) => {
            const safeName = file.name.replace(/[^\w.\-]+/g, "_");
            const path = `posts/${uid}/${Date.now()}_${safeName}`;
            const storageRef = ref(storage, path);

            return new Promise((resolve, reject) => {
                const task = uploadBytesResumable(storageRef, file);

                task.on(
                    "state_changed",
                    () => {
                    },
                    reject,
                    async () => {
                        const url = await getDownloadURL(task.snapshot.ref);
                        resolve({
                            url,
                            mimeType: file.type || "",
                            name: file.name || "",
                        });
                    }
                );
            });
        });

        return Promise.all(uploads);
    };


    const uploadCommentImages = async (uid, postId, imageFiles) => {
        if (!imageFiles?.length) return [];

        const next = Array.from(imageFiles);

        if (next.length > MAX_COMMENT_IMAGES) {
            throw new Error(`Max ${MAX_COMMENT_IMAGES} images per comment.`);
        }

        for (const f of next) {
            if (!String(f.type || "").startsWith("image/")) {
                throw new Error("Comments can include photos only (no video/audio).");
            }
            if (bytesToMB(f.size) > MAX_IMAGE_MB) {
                throw new Error(`Each image must be ≤ ${MAX_IMAGE_MB}MB.`);
            }
        }

        const uploads = next.map((file) => {
            const safeName = file.name.replace(/[^\w.\-]+/g, "_");
            const path = `comments/${uid}/${postId}/${Date.now()}_${safeName}`;
            const storageRef = ref(storage, path);

            return new Promise((resolve, reject) => {
                const task = uploadBytesResumable(storageRef, file);
                task.on(
                    "state_changed",
                    () => {
                    },
                    reject,
                    async () => {
                        const url = await getDownloadURL(task.snapshot.ref);
                        resolve({url, mimeType: file.type || "", name: file.name || ""});
                    }
                );
            });
        });

        return Promise.all(uploads);
    };

    const normalizedPostStyle = useMemo(() => normalizePostStyle(postStyle), [postStyle]);

    const hasDraftInput = useMemo(() => {
        if (isComposerEditing) return false;
        const gate = normalizeGateForPost(gateInput);
        const defaultGate = { logic: "any", gates: [{ type: "public" }] };
        const hasGateChanges = JSON.stringify(gate) != JSON.stringify(defaultGate);
        return Boolean(
            trimmedContent ||
            trimmedSummaryOverride ||
            parsedTags.length ||
            files.length ||
            hasGateChanges ||
            normalizedPostStyle
        );
    }, [
        isComposerEditing,
        trimmedContent,
        trimmedSummaryOverride,
        parsedTags,
        files,
        gateInput,
        normalizedPostStyle,
    ]);

    useEffect(() => {
        if (!canPost || !hasDraftInput || isComposerEditing) {
            setLocalDraftPost(null);
            draftCreatedAtRef.current = null;
            return;
        }

        if (!draftCreatedAtRef.current) {
            draftCreatedAtRef.current = new Date();
        }

        const mediaKind = detectMediaKind(files);
        const inferredType = objectTypeFromMediaKind(mediaKind, Boolean(trimmedContent));
        const finalSummary = trimmedSummaryOverride || clampSummaryFromContent(trimmedContent, 100);

        const obj = {
            type: inferredType,
            content: trimmedContent,
            summary: finalSummary || "",
            media: localFileMedia,
            url: extractedUrl || "",
            tags: parsedTags,
        };

        const preview = {
            text: makePreviewText({content: trimmedContent, summary: finalSummary}),
            imageUrl: mediaKind === "image" && localFileMedia?.[0]?.url ? localFileMedia[0].url : "",
        };

        setLocalDraftPost({
            id: "local-draft",
            authorUid: authUid || "",
            authorMuultiName: userDoc?.muultiName || "",
            createdAt: draftCreatedAtRef.current,
            updatedAt: new Date(),
            visibility: "public",
            audience: {to: ["public"], cc: []},
            object: obj,
            gate: normalizeGateForPost(gateInput),
            preview,
            status: "draft",
            style: normalizedPostStyle || undefined,
        });
    }, [
        canPost,
        hasDraftInput,
        isComposerEditing,
        files,
        trimmedContent,
        trimmedSummaryOverride,
        parsedTags,
        extractedUrl,
        gateInput,
        normalizedPostStyle,
        localFileMedia,
        authUid,
        userDoc,
    ]);

    const handlePostStyleChange = useCallback((nextStyle) => {
        setPostStyle(nextStyle);
    }, []);

    const handleUploadPostBackground = async (file, type = "image") => {
        if (!file) return;
        const previewUrl = URL.createObjectURL(file);
        if (backgroundPreviewUrlRef.current) {
            URL.revokeObjectURL(backgroundPreviewUrlRef.current);
        }
        backgroundPreviewUrlRef.current = previewUrl;
        setPostStyle((prev) => ({
            ...prev,
            backgroundType: type,
            backgroundImage: `url(${previewUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            repeatMode: "no-repeat",
        }));
        try {
            const uid = await requireDeterministicAuth();
            const safeName = file.name.replace(/[^\w.\-]+/g, "_");
            const path = `postBackgrounds/${uid}/${Date.now()}_${safeName}`;
            const storageRef = ref(storage, path);
            await new Promise((resolve, reject) => {
                const task = uploadBytesResumable(storageRef, file);
                task.on("state_changed", null, reject, () => resolve());
            });
            const url = await getDownloadURL(storageRef);
            setPostStyle((prev) => ({
                ...prev,
                backgroundType: type,
                backgroundImage: `url(${url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                repeatMode: "no-repeat",
            }));
            if (backgroundPreviewUrlRef.current === previewUrl) {
                URL.revokeObjectURL(previewUrl);
                backgroundPreviewUrlRef.current = null;
            }
        } catch (e) {
            console.error(e);
            setFormErr("Background upload failed.");
        }
    };

    const handleUploadPostCover = async (file) => {
        return handleUploadPostBackground(file, "cover");
    };

    const handleUpdatePostFromComposer = async (postId) => {
        setFormErr("");
        setMediaErr("");

        if (!canPost) {
            setFormErr("Connect a wallet and register your MuultiSpace username first.");
            return;
        }

        const gate = normalizeGateForPost(gateInput);

        // Validate gate config. Only list-backed types require listId.
        const needsList = (g) =>
            g?.type === "token" || g?.type === "collection" || g?.type === "compressedCollection";

        const hasNonPublic = gate.gates.some((g) => g?.type !== "public");
        const missingList = gate.gates.some((g) => needsList(g) && !g.listId);

        if (hasNonPublic && missingList) {
            setFormErr("Select a saved gate list for each token/collection gate.");
            return;
        }

        // Donation gates require a campaign/component id and a positive threshold.
        const hasDonation = gate.gates.some((g) => g?.type === "donation");
        const badDonation = gate.gates.some((g) => {
            if (g?.type !== "donation") return false;
            const compId = String(g?.donationCompId || "").trim();
            const threshold = Number(g?.donationThreshold || 0);
            return !compId || !(threshold > 0);
        });

        if (hasDonation && badDonation) {
            setFormErr("Donation gate needs a Campaign ID and a threshold greater than 0.");
            return;
        }

        const badTokensHeld = gate.gates.some((g) => {
            if (g?.type !== "tokensHeld") return false;
            return !(Number(g?.tokenThreshold || 0) > 0) || !String(g?.currency || "").trim();
        });

        if (badTokensHeld) {
            setFormErr("Tokens Held gate needs a token and a threshold greater than 0.");
            return;
        }

        setSubmitting(true);
        try {
            const uid = await requireDeterministicAuth();

            const existing = posts.find((p) => p.id === postId);
            if (!existing) throw new Error("Post not found.");
            if (existing.authorUid !== uid) throw new Error("You can only edit your own posts.");

            const nextMedia = files.length ? await uploadMediaFiles(uid, files) : (existing.object?.media || []);
            const mediaKind = files.length ? detectMediaKind(files) : "none";
            const inferredType = files.length
                ? objectTypeFromMediaKind(mediaKind, Boolean(trimmedContent))
                : existing.object?.type || objectTypeFromMediaKind("none", Boolean(trimmedContent));

            const finalSummary = trimmedSummaryOverride || clampSummaryFromContent(trimmedContent, 100);

            const nextObject = {
                ...(existing.object || {}),
                type: inferredType,
                content: trimmedContent,
                summary: finalSummary || "",
                media: nextMedia,
                url: extractedUrl || "",
                tags: parsedTags,
            };

            const nextPreview = {
                ...(existing.preview || {}),
                text: makePreviewText({content: trimmedContent, summary: finalSummary}),
            };

            if (files.length) {
                nextPreview.imageUrl = mediaKind === "image" && nextMedia?.[0]?.url ? nextMedia[0].url : "";
            }

            const normalizedStyle = normalizePostStyle(postStyle);
            const updatePayload = {
                object: nextObject,
                preview: nextPreview,
                gate,
                updatedAt: serverTimestamp(),
                style: normalizedStyle || null,
            };

            await updateDoc(doc(db, "posts", postId), updatePayload);


            setComposerEditPostId(null);
            resetComposerForm();
            await loadFeed({reset: true});
            window.scrollTo({top: 0, behavior: "smooth"});
        } catch (e) {
            console.error(e);
            setFormErr(e?.message || "Failed to update entry.");
        } finally {
            setSubmitting(false);
        }
    };


    const handleCreatePost = async () => {
        if (composerEditPostId) {
            const existing = posts.find((p) => p.id === composerEditPostId);
            if (!existing) {
                setComposerEditPostId(null);
            } else {
                await handleUpdatePostFromComposer(composerEditPostId);
                return;
            }
        }
        setFormErr("");
        setMediaErr("");

        if (!canPost) {
            setFormErr("Connect a wallet and register your MuultiSpace username first.");
            return;
        }

        // const trimmedContent = (content || "").trim();
        // const trimmedSummaryOverride = (summaryOverride || "").trim();
        //
        // if (!trimmedContent && !files.length) {
        //     setFormErr("Add some text content or upload media.");
        //     return;
        // }

        const gate = normalizeGateForPost(gateInput);
// Validate gate config. Only list-backed types require listId.
        const needsList = (g) =>
            g?.type === "token" || g?.type === "collection" || g?.type === "compressedCollection";

        const hasNonPublic = gate.gates.some((g) => g?.type !== "public");
        const missingList = gate.gates.some((g) => needsList(g) && !g.listId);

        if (hasNonPublic && missingList) {
            setFormErr("Select a saved gate list for each token/collection gate.");
            return;
        }

// Donation gates require a campaign/component id and a positive threshold.
        const hasDonation = gate.gates.some((g) => g?.type === "donation");
        const badDonation = gate.gates.some((g) => {
            if (g?.type !== "donation") return false;
            const compId = String(g?.donationCompId || "").trim();
            const threshold = Number(g?.donationThreshold || 0);
            return !compId || !(threshold > 0);
        });

        if (hasDonation && badDonation) {
            setFormErr("Donation gate needs a Campaign ID and a threshold greater than 0.");
            return;
        }

        const badTokensHeld = gate.gates.some((g) => {
            if (g?.type !== "tokensHeld") return false;
            return !(Number(g?.tokenThreshold || 0) > 0) || !String(g?.currency || "").trim();
        });

        if (badTokensHeld) {
            setFormErr("Tokens Held gate needs a token and a threshold greater than 0.");
            return;
        }


        setSubmitting(true);
        try {
            const uid = await requireDeterministicAuth();

            const authorMuultiName = userDoc.muultiName;

            const media = await uploadMediaFiles(uid, files);
            const mediaKind = detectMediaKind(files);
            const inferredType = objectTypeFromMediaKind(mediaKind, Boolean(trimmedContent));

            const finalSummary = trimmedSummaryOverride || clampSummaryFromContent(trimmedContent, 100);

            const obj = {
                type: inferredType,
                content: trimmedContent,
                summary: finalSummary || "",
                media,
                url: extractedUrl || "",
                tags: parsedTags,
            };


            const preview = {
                text: makePreviewText({content: trimmedContent, summary: finalSummary}),
                imageUrl: mediaKind === "image" && media?.[0]?.url ? media[0].url : "",
            };

            const normalizedStyle = normalizePostStyle(postStyle);
            const postData = {
                authorUid: uid,
                authorMuultiName,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                visibility: "public",
                audience: {to: ["public"], cc: []},
                object: obj,
                gate,
                preview,
            };
            if (normalizedStyle) postData.style = normalizedStyle;
            await addDoc(collection(db, "posts"), postData);

            resetComposerForm();

            await loadFeed({reset: true});
            window.scrollTo({top: 0, behavior: "smooth"});
        } catch (e) {
            console.error(e);
            setFormErr("Failed to publish entry.");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const donationSelected = hasDonationGate(gateInput);

        // Only auto-fill if:
        // - user has selected a donation campaign for gating
        // - summary is still empty
        // - user hasn't manually edited summary
        if (donationSelected && !summaryTouched && !(summaryOverride || "").trim()) {
            setSummaryOverride(SECRET_SUMMARY_DEFAULT);
            // do NOT set summaryTouched here; this is an auto-fill
        }
    }, [gateInput, summaryTouched, summaryOverride]);

    const handleSaveEditPost = async (postId) => {
        setEditErr("");
        setEditLoading(true);

        try {
            const uid = await requireDeterministicAuth();

            const existing = posts.find((p) => p.id === postId);
            if (!existing) throw new Error("Post not found.");
            if (existing.authorUid !== uid) throw new Error("You can only edit your own posts.");

            const trimmedContent = (editContent || "").trim();
            const trimmedSummaryOverride = (editSummaryOverride || "").trim();
            if (!trimmedContent) throw new Error("Content cannot be empty.");

            const nextTags = parseTagsInput(editTagsInput);



            const finalSummary = trimmedSummaryOverride || clampSummaryFromContent(trimmedContent, 100);

            const nextObject = {
                ...(existing.object || {}),
                content: trimmedContent,
                summary: finalSummary || "",
                url: extractFirstUrl(trimmedContent) || "",
                tags: nextTags,
            };

            const nextPreview = {
                ...(existing.preview || {}),
                text: makePreviewText({content: trimmedContent, summary: finalSummary}),
            };

            await updateDoc(doc(db, "posts", postId), {
                object: nextObject,
                preview: nextPreview,
                updatedAt: serverTimestamp(),
            });



            cancelEditPost();
            await loadFeed({reset: true});
        } catch (e) {
            console.error(e);
            setEditErr(e?.message || "Failed to update post.");
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeletePost = async (postId) => {
        const ok = window.confirm("Delete this post? This cannot be undone.");
        if (!ok) return;

        try {
            const uid = await requireDeterministicAuth();

            const existing = posts.find((p) => p.id === postId);
            if (!existing) throw new Error("Post not found.");
            if (existing.authorUid !== uid) throw new Error("You can only delete your own posts.");



            await deleteDoc(doc(db, "posts", postId));
            await loadFeed({reset: true});
        } catch (e) {
            console.error(e);
            setFeedErr(e?.message || "Failed to delete post.");
        }
    };

    useEffect(() => {
        if (isDetailView && !feedLoading) {
            const status = displayPosts.length > 0 ? 200 : feedErr ? 503 : 404;
            applyPageMetadata(null, pathname, { status });
        }
    }, [isDetailView, feedLoading, feedErr, displayPosts.length, pathname]);

    const emptyMessage = isDetailView
        ? "Post not found."
        : isFeedSearchActive
            ? "No posts match that search."
            : isFollowingFeedActive && !walletStr
                ? "Connect a wallet to filter by followed spaces."
            : isFollowingFeedActive && followingPublicKeys.length === 0
                ? "Follow spaces to build your social feed."
            : isFollowingFeedActive
                ? "No followed spaces have posted yet."
            : emptyMessageOverride || (isAuthorScoped ? "No social posts yet." : "No posts yet.");

    return (
        <div className={embedded ? "logfeed-embedded" : "muulti-container"}>
            {!embedded && <h1>{title}</h1>}
            {!embedded && !isDetailView && subtitle && <p>{subtitle}</p>}

            {/* ✅ Desktop grid wrapper (sidebar + feed). On mobile, CSS keeps it stacked. */}
            <div className={`logfeed-layout ${composerCollapsed ? "is-collapsed" : ""} ${isDetailView ? "is-detail" : ""} ${hideComposer ? "no-sidebar" : ""}`}>
                {/* ===== LEFT: composer/sidebar ===== */}
                {!isDetailView && !hideComposer && (
                <aside className={`logfeed-sidebar ${composerCollapsed ? "collapsed" : ""}`}>
                    {/* Desktop-only collapse toggle (CSS shows/hides by breakpoint) */}
                    <div className="logfeed-sidebar-topbar">
                        <button
                            type="button"
                            className="muulti-button logfeed-collapse-btn"
                            onClick={() => setComposerCollapsed((v) => !v)}
                            aria-expanded={!composerCollapsed}
                            aria-label={composerCollapsed ? "Expand create entry panel" : "Collapse create entry panel"}
                        >
                            {composerCollapsed ? "▶ Create Log" : "◀ Hide"}
                        </button>
                        {/*{!composerCollapsed ? <div className="logfeed-sidebar-title">Create</div> : null}*/}
                    </div>

                    {!composerCollapsed ? (
                        <>
                            {!connected && (
                                <div className="component-item locked">
                                    <h3>🔒 Connect your wallet to create an entry.</h3>
                                    <p>You can still browse the public feed below.</p>
                                </div>
                            )}

                            {connected && userLoading &&
                            <div className="component-item"><p>Loading your profile...</p></div>}

                            {connected && !userLoading && userErr && (
                                <div className="component-item locked">
                                    <p className="error">{userErr}</p>
                                </div>
                            )}

                            {connected && !userLoading && !userErr && !userDoc && (
                                <div className="component-item locked">
                                    <h3>🔒 No MuultiSpace profile found.</h3>
                                    <p>Create your space first, then come back to publish logs.</p>
                                    <Link className="muulti-button" to="/">
                                        Go to Muulti Editor
                                    </Link>
                                </div>
                            )}

                            {connected && !userLoading && userDoc && !userDoc.muultiName && (
                                <div className="component-item locked">
                                    <h3>🔒 You need a MuultiSpace username before posting.</h3>
                                    <p>Register your space name in the editor first.</p>
                                    <Link className="muulti-button" to="/">
                                        Go to Muulti Editor
                                    </Link>
                                </div>
                            )}

                            {canPost && (
                                <>
                                    <div className="component-item logfeed-composer">
                                        {isComposerEditing && (
                                            <div className="logfeed-editing-banner">
                                                <div className="logfeed-editing-text">
                                                    <strong>Editing entry</strong>
                                                    {editingComposerPost?.createdAt ? (
                                                        <span>
                                                            {" "}Created {formatTs(editingComposerPost.createdAt)}.
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="muulti-button"
                                                    onClick={cancelComposerEdit}
                                                >
                                                    Cancel Edit
                                                </button>
                                            </div>
                                        )}
                                        <h2>{isComposerEditing ? "Edit Log / Entry" : "Create a Log / Entry"}</h2>

                                        <label>
                                            Content
                                            <textarea
                                                className="muulti-textarea"
                                                value={content}
                                                onChange={(e) => setContent(e.target.value)}
                                                placeholder="Write your log entry..."
                                            />
                                        </label>

                                        <label>
                                            Summary (optional override)
                                            <textarea
                                                className="muulti-textarea"
                                                value={summaryOverride}
                                                onChange={(e) => {
                                                    setSummaryTouched(true);
                                                    setSummaryOverride(e.target.value);
                                                }}
                                                placeholder="If empty, we'll use the first 100 characters of content."
                                            />
                                        </label>

                                        <label>
                                            Upload media (optional)
                                            <input
                                                className="muulti-input"
                                                type="file"
                                                accept="image/*,audio/*,video/*"
                                                multiple
                                                onChange={(e) => onFilesSelected(e.target.files)}
                                            />
                                            <small>
                                                Rules: max {MAX_IMAGES} images (&lt;= {MAX_IMAGE_MB}MB each), or 1 audio
                                                (&lt;= {MAX_AUDIO_MB}MB), or 1 video (&lt;= {MAX_VIDEO_MB}MB).
                                            </small>
                                            {isComposerEditing && (
                                                <small>
                                                    Editing note: existing media stays unless you upload new files.
                                                </small>
                                            )}
                                        </label>

                                        {mediaErr && <p className="error">{mediaErr}</p>}

                                        {files.length > 0 && (
                                            <div style={{marginTop: 10}}>
                                                <strong>Selected:</strong>
                                                <ul>
                                                    {files.map((f, i) => (
                                                        <li key={i}>
                                                            {f.name} ({bytesToMB(f.size).toFixed(2)}MB)
                                                            {" - "} {f.type || "unknown"}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        <label>
                                            Tags (comma separated)
                                            <input
                                                className="muulti-input"
                                                type="text"
                                                value={tagsInput}
                                                onChange={(e) => setTagsInput(e.target.value)}
                                                placeholder="muultispace, devlog, drop"
                                            />
                                        </label>

                                        <GatePicker
                                            value={gateInput}
                                            onChange={handleGateInputChange}
                                            donationCampaignOptions={donationCampaignOptions}
                                        />
                                    </div>
                                    <div style={{ marginTop: 12 }}>
                                        <PostStyleControls
                                            style={postStyle}
                                            onStyleChange={handlePostStyleChange}
                                            onBackgroundImageChange={handleUploadPostBackground}
                                            onCoverChange={handleUploadPostCover}
                                        />
                                    </div>
                                    <div style={{ marginTop: 12 }}>
                                        {formErr && <p className="error">{formErr}</p>}
                                        <div className="muulti-container-button-container">
                                            <button
                                                className="muulti-button"
                                                onClick={handleCreatePost}
                                                disabled={submitting}
                                            >
                                                {submitting
                                                    ? (isComposerEditing ? "Saving..." : "Publishing...")
                                                    : (isComposerEditing ? "Save Changes" : "Publish Entry")}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (




                        // collapsed "rail" (desktop only)
                        <div className="logfeed-sidebar-collapsed-label"></div>
                    )}
                </aside>
                )}

                {/* ===== RIGHT: feed ===== */}
                <main className="logfeed-main">
                    <h2>{isDetailView ? "Post Detail" : (feedTitle || (isAuthorScoped ? "Social Posts" : "Public Social Feed"))}</h2>
                    {!isDetailView && (
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 10,
                                marginTop: 8,
                            }}
                        >
                            <form className="users-directory-search logfeed-search" onSubmit={handleFeedSearchSubmit}>
                                <div className="logfeed-search-row">
                                    <input
                                        className="users-directory-search-input logfeed-search-input"
                                        type="search"
                                        value={feedSearchInput}
                                        onChange={(e) => setFeedSearchInput(e.target.value)}
                                        placeholder="Search feed"
                                        aria-label="Search social feed"
                                    />
                                    <button className="muulti-button logfeed-control-button" type="submit">
                                        Search
                                    </button>
                                    {(feedSearchInput || isFeedSearchActive) && (
                                        <button
                                            className="users-directory-clear logfeed-control-button logfeed-clear-button"
                                            type="button"
                                            onClick={handleClearFeedSearch}
                                            aria-label="Clear feed search"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                <div className="logfeed-search-scopes" role="group" aria-label="Search filters">
                                    <label className="logfeed-search-scope">
                                        <input
                                            type="checkbox"
                                            checked={feedSearchScopes.keywords}
                                            onChange={() => toggleFeedSearchScope("keywords")}
                                        />
                                        Keywords
                                    </label>
                                    <label className="logfeed-search-scope">
                                        <input
                                            type="checkbox"
                                            checked={feedSearchScopes.tags}
                                            onChange={() => toggleFeedSearchScope("tags")}
                                        />
                                        Tags
                                    </label>
                                    <label className="logfeed-search-scope">
                                        <input
                                            type="checkbox"
                                            checked={feedSearchScopes.accounts}
                                            onChange={() => toggleFeedSearchScope("accounts")}
                                        />
                                        Accounts
                                    </label>
                                </div>
                            </form>
                            {!isAuthorScoped && (
                                <div style={{display: "inline-flex", gap: 8}}>
                                    <button
                                        type="button"
                                        className="muulti-button logfeed-control-button"
                                        onClick={() => handleFeedScopeChange("public")}
                                        disabled={feedScope === "public"}
                                    >
                                        All Posts
                                    </button>
                                    <button
                                        type="button"
                                        className="muulti-button logfeed-control-button"
                                        onClick={() => handleFeedScopeChange("following")}
                                        disabled={feedScope === "following"}
                                    >
                                        Following
                                    </button>
                                </div>
                            )}
                            <div style={{display: "inline-flex", gap: 8}}>
                                <button
                                    type="button"
                                    className="muulti-button logfeed-control-button"
                                    onClick={() => handleFeedModeChange("pagination")}
                                    disabled={feedMode === "pagination"}
                                >
                                    Pages
                                </button>
                                <button
                                    type="button"
                                    className="muulti-button logfeed-control-button"
                                    onClick={() => handleFeedModeChange("infinite")}
                                    disabled={feedMode === "infinite"}
                                >
                                    Infinite Scroll
                                </button>
                            </div>
                            {feedMode === "pagination" ? (
                                <div style={{display: "inline-flex", alignItems: "center", gap: 8}}>
                                    <button
                                        className="users-directory-page-btn"
                                        type="button"
                                        onClick={goToPrevPage}
                                        disabled={feedLoading || !canGoPrevPage}
                                    >
                                        Previous
                                    </button>
                                    <span className="users-directory-page-info">Page {currentPage}</span>
                                    <button
                                        className="users-directory-page-btn"
                                        type="button"
                                        onClick={goToNextPage}
                                        disabled={feedLoading || !canGoNextPage}
                                    >
                                        Next
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    )}
                    {!isDetailView && isFeedSearchActive && (
                        <div className="logfeed-search-status" aria-live="polite">
                            Search: {appliedFeedSearchLabel} in {appliedFeedSearchScopeLabel}
                        </div>
                    )}
                    {!isDetailView && isFollowingFeedActive && (
                        <div className="logfeed-search-status" aria-live="polite">
                            {followingLoading
                                ? "Loading followed spaces..."
                                : followingErr || `Following feed: ${followingPublicKeys.length} ${followingPublicKeys.length === 1 ? "space" : "spaces"}`}
                        </div>
                    )}

                    {feedLoading && (
                        <div className="component-item">
                            <p>Loading feed…</p>
                        </div>
                    )}

                    {feedErr && (
                        isDetailView && displayPosts.length === 0 ? <PageUnavailable /> :
                        <div className="component-item locked">
                            <p className="error">{feedErr}</p>
                        </div>
                    )}

                    {!feedLoading && !feedErr && displayPosts.length === 0 && (
                        isDetailView ? <NotFoundPage /> :
                        <div className="component-item">
                            <p>{emptyMessage}</p>
                        </div>
                    )}

                    <div className="logfeed-list">
                        {resolvedDisplayPosts.map((p) => (
                            <LogFeedPostCard
                                key={p.id}
                                p={p}
                                isOwnerOfPost={isOwnerOfPost}
                                beginEditPost={beginEditPost}
                                beginComposerEditPost={beginComposerEditPost}
                                setPostCardRef={(postId, el) => {
                                    if (el) postCardRefs.current[postId] = el;
                                    else delete postCardRefs.current[postId];
                                }}
                                handleCopyPostCard={handleCopyPostCard}
                                handleDownloadPostCard={handleDownloadPostCard}
                                sharingPostId={sharingPostId}
                                handleDeletePost={handleDeletePost}
                                openMenuPostId={openMenuPostId}
                                setOpenMenuPostId={setOpenMenuPostId}
                                donationLookup={donationLookup}
                                editingPostId={editingPostId}
                                cancelEditPost={cancelEditPost}
                                handleSaveEditPost={handleSaveEditPost}
                                editContent={editContent}
                                setEditContent={setEditContent}
                                editSummaryOverride={editSummaryOverride}
                                setEditSummaryOverride={setEditSummaryOverride}
                                editTagsInput={editTagsInput}
                                setEditTagsInput={setEditTagsInput}
                                editErr={editErr}
                                editLoading={editLoading}
                                formatTs={formatTs}
                                makePreviewText={makePreviewText}
                                setModalImage={setModalImage}
                                LogFeedImage={LogFeedImage}
                                showCommentsByPost={showCommentsByPost}
                                setShowCommentsByPost={setShowCommentsByPost}
                                loadCommentsForPost={loadCommentsForPost}
                                commentsByPost={resolvedCommentsByPost}
                                commentsLoadingByPost={commentsLoadingByPost}
                                commentErrByPost={commentErrByPost}
                                canComment={canComment}
                                canReact={canReact}
                                newCommentByPost={newCommentByPost}
                                setNewCommentByPost={setNewCommentByPost}
                                newCommentFilesByPost={newCommentFilesByPost}
                                setNewCommentFilesByPost={setNewCommentFilesByPost}
                                handleCreateComment={handleCreateComment}
                                editingCommentByPost={editingCommentByPost}
                                beginEditComment={beginEditComment}
                                cancelEditComment={cancelEditComment}
                                handleSaveEditComment={handleSaveEditComment}
                                handleDeleteComment={handleDeleteComment}
                                editCommentTextByPost={editCommentTextByPost}
                                setEditCommentTextByPost={setEditCommentTextByPost}
                                editCommentErrByPost={editCommentErrByPost}
                                editCommentLoadingByPost={editCommentLoadingByPost}
                                openMenuCommentKey={openMenuCommentKey}
                                setOpenMenuCommentKey={setOpenMenuCommentKey}
                                isOwnerOfComment={isOwnerOfComment}
                                reactionsByPost={reactionsByPost}
                                reactionsLoadingByPost={reactionsLoadingByPost}
                                reactionErrByPost={reactionErrByPost}
                                loadReactionsForPost={loadReactionsForPost}
                                handleSetReaction={handleSetReaction}
                                currentUid={authUid || ""}
                                authorProfilePicUrl={
                                    authorProfilesByUid[String(p?.authorUid || "").trim()]?.profilePicUrl ||
                                    String(p?.authorProfilePicUrl || "")
                                }
                                showFollowerCountForAuthor={
                                    authorProfilesByUid[String(p?.authorUid || "").trim()]?.showFollowerCount === true
                                }
                                followerMuultiName={userDoc?.muultiName || ""}
                                getPostDetailPath={getPostDetailPath}
                                handleSharePostLink={handleSharePostLink}
                                sharingLinkPostId={sharingLinkPostId}
                                linkCopiedPostId={linkCopiedPostId}
                                isDetailView={isDetailView}
                                onSearchTag={!isDetailView ? handleSearchTagClick : null}
                            />
                        ))}
                    </div>
                    {isDetailView && displayPosts.length > 0 && (
                        <div className="detail-page-return-link-container">
                            <Link className="muulti-button detail-page-return-link" to={backLinkPath}>
                                {backLinkLabel}
                            </Link>
                        </div>
                    )}

                    {!isDetailView && feedMode === "infinite" && hasMore && (
                        <div
                            className="logfeed-load-more"
                            ref={loadMoreRef}
                        >
                            {!supportsIO && !feedLoading && (
                                <button className="muulti-button logfeed-control-button" type="button" onClick={() => loadFeed({ reset: false })}>
                                    Load more
                                </button>
                            )}
                            {feedLoading && <span>Loading more</span>}
                        </div>
                    )}
                    {!isDetailView && feedMode === "pagination" && (
                        <div className="users-directory-pagination">
                            <button
                                className="users-directory-page-btn"
                                type="button"
                                onClick={goToPrevPage}
                                disabled={feedLoading || !canGoPrevPage}
                            >
                                Previous
                            </button>
                            <span className="users-directory-page-info">Page {currentPage}</span>
                            <button
                                className="users-directory-page-btn"
                                type="button"
                                onClick={goToNextPage}
                                disabled={feedLoading || !canGoNextPage}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </main>
            </div>

            {modalImage && (
                <ImageModal
                    isOpen={!!modalImage}
                    onClose={() => setModalImage(null)}
                    image={modalImage.image}
                    name={modalImage.name}
                />
            )}
        </div>
    );
}
